// ==========================================
// 2. CITY MAP & WORLD DEFINITION
// ==========================================

import { t } from '../i18n';
/** Afstand (px) waarop de politie voor je blijft staan bij een staandehouding. */
const POLICE_STAND_OFF = 105;
/** Katten stappen opzij als de scooter dichterbij komt dan dit. */
const CAT_PERSONAL_SPACE = 80;
/** Katten lopen rustiger dan voorheen. */
const CAT_WALK_FACTOR = 0.8;
/** Hooguit zoveel katten tegelijk onderweg over de rijbaan. */
const MAX_CROSSING_CATS = 2;
/** Je raakt een kat alleen als je sneller rijdt dan dit (stilstaan of kruipen is veilig). */
const CAT_HIT_MIN_SPEED = 0.9;

export class CityMap {
  constructor() {
    // Wereld 4x zo groot (4800 x 4800 px, oppervlakte 23.040.000 px² t.o.v. 5.760.000 px²)
    this.width = 4800;
    this.height = 4800;

    // Roads layout (asphalt rectangles over the entire 4800x4800 world)
    this.roads = [
      // Horizontale Avenues & Straten
      { x: 0, y: 350,  w: 4800, h: 140, name: 'Noorderlaan', lanes: 1 },
      { x: 0, y: 1100, w: 4800, h: 200, name: 'Hoofdstraat', lanes: 2 },
      { x: 0, y: 1850, w: 4800, h: 140, name: 'Singelweg', lanes: 1 },
      { x: 0, y: 2650, w: 4800, h: 180, name: 'Zuiderboulevard', lanes: 2 },
      { x: 0, y: 3450, w: 4800, h: 140, name: 'Industrieweg', lanes: 1 },
      { x: 0, y: 4250, w: 4800, h: 140, name: 'Havenring Zuid', lanes: 1 },

      // Verticale Boulevards & Straten
      { x: 350,  y: 0, w: 140, h: 4800, name: 'Klinkerstraat', lanes: 1, klinkers: true },
      { x: 1100, y: 0, w: 200, h: 4800, name: 'Bakkerijboulevard', lanes: 2 },
      { x: 1850, y: 0, w: 140, h: 4800, name: 'Oostwal', lanes: 1 },
      { x: 2650, y: 0, w: 180, h: 4800, name: 'Havenboulevard', lanes: 2 },
      { x: 3450, y: 0, w: 140, h: 4800, name: 'Universiteitslaan', lanes: 1 },
      { x: 4250, y: 0, w: 140, h: 4800, name: 'Oosterring', lanes: 1 }
    ];

    // Speed bumps (drempels over de hele stad)
    this.speedBumps = [
      { x: 650, y: 1100, w: 30, h: 200, orientation: 'vertical' },
      { x: 1550, y: 1100, w: 30, h: 200, orientation: 'vertical' },
      { x: 2250, y: 1100, w: 30, h: 200, orientation: 'vertical' },
      { x: 3050, y: 1100, w: 30, h: 200, orientation: 'vertical' },
      { x: 3850, y: 1100, w: 30, h: 200, orientation: 'vertical' },
      { x: 1100, y: 700, w: 200, h: 30, orientation: 'horizontal' },
      { x: 1100, y: 1550, w: 200, h: 30, orientation: 'horizontal' },
      { x: 1100, y: 2250, w: 200, h: 30, orientation: 'horizontal' },
      { x: 1100, y: 3100, w: 200, h: 30, orientation: 'horizontal' },
      { x: 1100, y: 3900, w: 200, h: 30, orientation: 'horizontal' },
      { x: 350, y: 800, w: 140, h: 25, orientation: 'horizontal' },
      { x: 350, y: 2250, w: 140, h: 25, orientation: 'horizontal' },
      { x: 350, y: 3850, w: 140, h: 25, orientation: 'horizontal' },
      { x: 1850, y: 1400, w: 140, h: 25, orientation: 'horizontal' },
      { x: 1850, y: 3100, w: 140, h: 25, orientation: 'horizontal' },
      { x: 2650, y: 800, w: 180, h: 30, orientation: 'horizontal' },
      { x: 2650, y: 3100, w: 180, h: 30, orientation: 'horizontal' },
      { x: 3450, y: 1550, w: 140, h: 25, orientation: 'horizontal' },
      { x: 3450, y: 3900, w: 140, h: 25, orientation: 'horizontal' },
      { x: 4250, y: 800, w: 140, h: 25, orientation: 'horizontal' },
      { x: 4250, y: 2250, w: 140, h: 25, orientation: 'horizontal' }
    ];

    // Water puddles (causing slip)
    this.puddles = [
      { x: 750, y: 1220, r: 40 },
      { x: 1400, y: 1150, r: 48 },
      { x: 2200, y: 1160, r: 42 },
      { x: 3100, y: 1160, r: 45 },
      { x: 3900, y: 1220, r: 44 },
      { x: 1160, y: 1700, r: 35 },
      { x: 400, y: 600, r: 38 },
      { x: 1900, y: 950, r: 42 },
      { x: 750, y: 2740, r: 46 },
      { x: 1500, y: 2680, r: 44 },
      { x: 2300, y: 2740, r: 40 },
      { x: 3100, y: 2680, r: 45 },
      { x: 3900, y: 2740, r: 48 },
      { x: 800, y: 3520, r: 42 },
      { x: 2200, y: 3520, r: 40 },
      { x: 3700, y: 3520, r: 44 },
      { x: 1200, y: 4320, r: 45 },
      { x: 2700, y: 4320, r: 42 }
    ];

    // Street lamps with light radius (lit in night mode)
    this.streetLamps = [
      { x: 1060, y: 300 }, { x: 1340, y: 300 },
      { x: 1060, y: 800 }, { x: 1340, y: 800 },
      { x: 1060, y: 1400 }, { x: 1340, y: 1400 },
      { x: 1060, y: 1900 }, { x: 1340, y: 1900 },
      { x: 1060, y: 2500 }, { x: 1340, y: 2500 },
      { x: 1060, y: 3300 }, { x: 1340, y: 3300 },
      { x: 1060, y: 4100 }, { x: 1340, y: 4100 },
      { x: 300, y: 1050 }, { x: 300, y: 1350 },
      { x: 800, y: 1050 }, { x: 800, y: 1350 },
      { x: 1600, y: 1050 }, { x: 1600, y: 1350 },
      { x: 2100, y: 1050 }, { x: 2100, y: 1350 },
      { x: 2850, y: 1050 }, { x: 2850, y: 1350 },
      { x: 3650, y: 1050 }, { x: 3650, y: 1350 },
      { x: 4450, y: 1050 }, { x: 4450, y: 1350 },
      { x: 2600, y: 800 }, { x: 2880, y: 800 },
      { x: 2600, y: 2000 }, { x: 2880, y: 2000 },
      { x: 2600, y: 3300 }, { x: 2880, y: 3300 },
      { x: 3400, y: 800 }, { x: 3400, y: 2000 },
      { x: 3400, y: 3300 }, { x: 4200, y: 800 }
    ];

    // Bakery HQ and key buildings
    this.bakery = {
      x: 1320,
      y: 920,
      w: 220,
      h: 160,
      name: 'Knus & Krokant Hoofdkwartier',
      loadingZone: { x: 1240, y: 1020, w: 60, h: 70, name: 'Laadzone Bakkerij' }
    };

    // Buildings & residential plots - Verrijkt met authentieke Nederlandse trapgevels, klokgevels, luifels & bloembakken
    this.buildings = [
      // --- SECTOR 1: HISTORISCH CENTRUM & VILLAPARK ---
      { x: 120, y: 120, w: 200, h: 200, color: '#f8fafc', roof: '#475569', label: 'Woonwijk De Beuk', doorX: 220, doorY: 320, streetSide: 'south', district: 'Centrum', gable: 'trapgevel', brickColor: '#b91c1c', trimColor: '#ffffff', awning: 'green_white', windowCols: 3, windowRows: 2, flowerBoxes: true, chimney: true },
      { x: 520, y: 120, w: 240, h: 200, color: '#fef3c7', roof: '#9a3412', label: 'Familie Jansen (Nr. 4)', doorX: 640, doorY: 320, streetSide: 'south', district: 'Centrum', gable: 'klokgevel', brickColor: '#c2410c', trimColor: '#fef08a', awning: 'orange_white', windowCols: 4, windowRows: 2, flowerBoxes: true, chimney: true },
      { x: 800, y: 120, w: 260, h: 200, color: '#e0f2fe', roof: '#1e293b', label: 'Kantoor Horizon (Nr. 18)', doorX: 930, doorY: 320, streetSide: 'south', district: 'Centrum', gable: 'herenhuis', brickColor: '#78350f', trimColor: '#ffffff', awning: 'blue_white', windowCols: 4, windowRows: 2, flowerBoxes: false, chimney: true },
      { x: 1340, y: 120, w: 280, h: 200, color: '#fce7f3', roof: '#831843', label: 'Villa Rozenhof (Nr. 42)', doorX: 1480, doorY: 320, streetSide: 'south', district: 'Villapark', gable: 'klokgevel', brickColor: '#be185d', trimColor: '#fdf2f8', awning: 'orange_white', windowCols: 4, windowRows: 2, flowerBoxes: true, chimney: true },
      { x: 1660, y: 120, w: 160, h: 200, color: '#dcfce7', roof: '#14532d', label: 'Huisarts De Vries', doorX: 1740, doorY: 320, streetSide: 'south', district: 'Villapark', gable: 'tuitgevel', brickColor: '#15803d', trimColor: '#f0fdf4', awning: 'green_white', windowCols: 2, windowRows: 2, flowerBoxes: true, chimney: true },
      { x: 2020, y: 120, w: 260, h: 200, color: '#ede9fe', roof: '#4c1d95', label: 'Boetiek Koffiehoek', doorX: 2140, doorY: 320, streetSide: 'south', district: 'Villapark', gable: 'trapgevel', brickColor: '#6d28d9', trimColor: '#faf5ff', awning: 'orange_white', windowCols: 4, windowRows: 2, flowerBoxes: true, chimney: true },
      { x: 1570, y: 920, w: 250, h: 160, color: '#fef9c3', roof: '#854d0e', label: 'Grand Café Het Anker', doorX: 1650, doorY: 1080, streetSide: 'south', district: 'Centrum', gable: 'trapgevel', brickColor: '#9a3412', trimColor: '#fef3c7', awning: 'orange_white', windowCols: 4, windowRows: 2, flowerBoxes: true, chimney: true },
      { x: 120, y: 1330, w: 200, h: 240, color: '#f1f5f9', roof: '#334155', label: 'Studentenflat Campus', doorX: 320, doorY: 1420, streetSide: 'east', district: 'Campus', gable: 'herenhuis', brickColor: '#475569', trimColor: '#ffffff', awning: 'blue_white', windowCols: 3, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 520, y: 1330, w: 260, h: 240, color: '#ffedd5', roof: '#9a3412', label: 'Knus Expres Bakkerij & Depot', doorX: 650, doorY: 1330, streetSide: 'north', district: 'Centrum', gable: 'trapgevel', brickColor: '#ea580c', trimColor: '#ffedd5', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 820, y: 1330, w: 250, h: 240, color: '#ecfdf5', roof: '#064e3b', label: 'Meneer Van Dijk (Nr. 88)', doorX: 940, doorY: 1330, streetSide: 'north', district: 'Centrum', gable: 'klokgevel', brickColor: '#047857', trimColor: '#ecfdf5', awning: 'green_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 1340, y: 1330, w: 280, h: 240, color: '#fef2f2', roof: '#7f1d1d', label: 'Tandartspraktijk Wit', doorX: 1480, doorY: 1330, streetSide: 'north', district: 'Centrum', gable: 'herenhuis', brickColor: '#b91c1c', trimColor: '#ffffff', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: true },
      { x: 1660, y: 1330, w: 160, h: 240, color: '#f3e8ff', roof: '#581c87', label: 'Atelier Bloem', doorX: 1740, doorY: 1330, streetSide: 'north', district: 'Centrum', gable: 'trapgevel', brickColor: '#7e22ce', trimColor: '#faf5ff', awning: 'orange_white', windowCols: 2, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 2020, y: 1330, w: 260, h: 240, color: '#e0e7ff', roof: '#312e81', label: 'Notariskantoor Oost', doorX: 2150, doorY: 1330, streetSide: 'north', district: 'Centrum', gable: 'herenhuis', brickColor: '#4338ca', trimColor: '#e0e7ff', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },

      // --- SECTOR 2: HAVENKWARTIER & GRACHTEN ---
      { x: 2900, y: 120, w: 260, h: 200, color: '#e0f2fe', roof: '#0369a1', label: 'Visafslag De Zeelt', doorX: 3030, doorY: 320, streetSide: 'south', district: 'Havenkwartier', gable: 'trapgevel', brickColor: '#0284c7', trimColor: '#f0f9ff', awning: 'blue_white', windowCols: 4, windowRows: 2, flowerBoxes: false, chimney: true },
      { x: 3700, y: 120, w: 240, h: 200, color: '#fee2e2', roof: '#991b1b', label: 'Vuurtoren Havenlicht', doorX: 3820, doorY: 320, streetSide: 'south', district: 'Havenkwartier', gable: 'tuitgevel', brickColor: '#dc2626', trimColor: '#ffffff', awning: 'orange_white', windowCols: 3, windowRows: 2, flowerBoxes: true, chimney: true },
      { x: 4420, y: 120, w: 250, h: 200, color: '#f0fdf4', roof: '#14532d', label: 'Koninklijke Roeivereniging', doorX: 4540, doorY: 320, streetSide: 'south', district: 'Havenkwartier', gable: 'herenhuis', brickColor: '#16a34a', trimColor: '#ffffff', awning: 'green_white', windowCols: 4, windowRows: 2, flowerBoxes: true, chimney: false },
      { x: 2100, y: 600, w: 260, h: 220, color: '#ffedd5', roof: '#7c2d12', label: 'Pakhuizen De Zwaan', doorX: 2230, doorY: 820, streetSide: 'south', district: 'Havenkwartier', gable: 'trapgevel', brickColor: '#c2410c', trimColor: '#fef08a', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: true },
      { x: 2900, y: 600, w: 280, h: 220, color: '#ede9fe', roof: '#4c1d95', label: 'Maritiem Museum', doorX: 3040, doorY: 820, streetSide: 'south', district: 'Havenkwartier', gable: 'herenhuis', brickColor: '#6d28d9', trimColor: '#ffffff', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 3700, y: 600, w: 240, h: 220, color: '#fef9c3', roof: '#713f12', label: 'Brasserie Zeewind', doorX: 3820, doorY: 820, streetSide: 'south', district: 'Havenkwartier', gable: 'klokgevel', brickColor: '#a16207', trimColor: '#fef08a', awning: 'orange_white', windowCols: 3, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 4420, y: 600, w: 250, h: 220, color: '#f1f5f9', roof: '#0f172a', label: 'Scheepswerf De Boeg', doorX: 4545, doorY: 820, streetSide: 'south', district: 'Havenkwartier', gable: 'tuitgevel', brickColor: '#334155', trimColor: '#cbd5e1', awning: null, windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },

      // --- SECTOR 3: CULTUURWIJK & OOSTERPARK ---
      { x: 2900, y: 1330, w: 280, h: 240, color: '#fae8ff', roof: '#701a75', label: 'Stadsschouwburg & Theater', doorX: 3040, doorY: 1330, streetSide: 'north', district: 'Cultuurwijk', gable: 'herenhuis', brickColor: '#a21caf', trimColor: '#fdf4ff', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 3700, y: 1330, w: 260, h: 240, color: '#fef08a', roof: '#713f12', label: 'Concertgebouw De Gouden Noot', doorX: 3830, doorY: 1330, streetSide: 'north', district: 'Cultuurwijk', gable: 'klokgevel', brickColor: '#ca8a04', trimColor: '#ffffff', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 4420, y: 1330, w: 240, h: 240, color: '#e0e7ff', roof: '#312e81', label: 'Galerie Moderne Meesters', doorX: 4540, doorY: 1330, streetSide: 'north', district: 'Cultuurwijk', gable: 'trapgevel', brickColor: '#4338ca', trimColor: '#e0e7ff', awning: 'green_white', windowCols: 3, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 2100, y: 2100, w: 260, h: 240, color: '#fce7f3', roof: '#831843', label: 'Grand Hotel Majestic', doorX: 2230, doorY: 2100, streetSide: 'north', district: 'Cultuurwijk', gable: 'herenhuis', brickColor: '#be185d', trimColor: '#fdf2f8', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 2900, y: 2100, w: 280, h: 240, color: '#f1f5f9', roof: '#1e293b', label: 'Rijksarchief & Documentatie', doorX: 3040, doorY: 2100, streetSide: 'north', district: 'Cultuurwijk', gable: 'trapgevel', brickColor: '#334155', trimColor: '#f8fafc', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: true },
      { x: 3700, y: 2100, w: 260, h: 240, color: '#ccfbf1', roof: '#134e4a', label: 'Wellness & Spa De Bron', doorX: 3830, doorY: 2100, streetSide: 'north', district: 'Cultuurwijk', gable: 'klokgevel', brickColor: '#0f766e', trimColor: '#f0fdfa', awning: 'green_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 4420, y: 2100, w: 250, h: 240, color: '#fef3c7', roof: '#78350f', label: 'Pleinbistro De Drie Zonnen', doorX: 4545, doorY: 2100, streetSide: 'north', district: 'Cultuurwijk', gable: 'trapgevel', brickColor: '#b45309', trimColor: '#fef08a', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },

      // --- SECTOR 4: AMBACHT & INDUSTRIEPARK ---
      { x: 120,  y: 2100, w: 200, h: 240, color: '#f8fafc', roof: '#334155', label: 'Meubelfabriek De Eik', doorX: 220, doorY: 2100, streetSide: 'north', district: 'Industrie', gable: 'tuitgevel', brickColor: '#64748b', trimColor: '#f1f5f9', awning: null, windowCols: 3, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 520,  y: 2100, w: 260, h: 240, color: '#fee2e2', roof: '#7f1d1d', label: 'Brandweerkazerne Zuid', doorX: 650, doorY: 2100, streetSide: 'north', district: 'Industrie', gable: 'tuitgevel', brickColor: '#b91c1c', trimColor: '#fee2e2', awning: null, windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 820,  y: 2100, w: 250, h: 240, color: '#f1f5f9', roof: '#1e293b', label: 'Drukkerij Snelschrift', doorX: 940, doorY: 2100, streetSide: 'north', district: 'Industrie', gable: 'trapgevel', brickColor: '#475569', trimColor: '#ffffff', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: true },
      { x: 1340, y: 2100, w: 280, h: 240, color: '#fef3c7', roof: '#78350f', label: 'Distributie Post & Pakket', doorX: 1480, doorY: 2100, streetSide: 'north', district: 'Industrie', gable: 'herenhuis', brickColor: '#d97706', trimColor: '#fef3c7', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 120,  y: 2900, w: 200, h: 240, color: '#fef9c3', roof: '#713f12', label: 'Brouwerij Het Gouden Hop', doorX: 220, doorY: 2900, streetSide: 'north', district: 'Industrie', gable: 'trapgevel', brickColor: '#854d0e', trimColor: '#fef08a', awning: 'green_white', windowCols: 3, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 520,  y: 2900, w: 260, h: 240, color: '#e0f2fe', roof: '#075985', label: 'Koelopslag Polar Vries', doorX: 650, doorY: 2900, streetSide: 'north', district: 'Industrie', gable: 'tuitgevel', brickColor: '#0284c7', trimColor: '#f0f9ff', awning: null, windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 820,  y: 2900, w: 250, h: 240, color: '#f3f4f6', roof: '#111827', label: 'Autogarage Van Dam & Zn.', doorX: 940, doorY: 2900, streetSide: 'north', district: 'Industrie', gable: 'tuitgevel', brickColor: '#1f2937', trimColor: '#9ca3af', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 1340, y: 2900, w: 280, h: 240, color: '#ffedd5', roof: '#7c2d12', label: 'Bouwmarkt De Gouden Spijker', doorX: 1480, doorY: 2900, streetSide: 'north', district: 'Industrie', gable: 'tuitgevel', brickColor: '#ea580c', trimColor: '#fed7aa', awning: null, windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 2100, y: 2900, w: 260, h: 240, color: '#ecfdf5', roof: '#064e3b', label: 'Stadskwekerij Groenland', doorX: 2230, doorY: 2900, streetSide: 'north', district: 'Industrie', gable: 'klokgevel', brickColor: '#059669', trimColor: '#d1fae5', awning: 'green_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 120,  y: 3700, w: 200, h: 240, color: '#f0fdf4', roof: '#14532d', label: 'Milieustraat & Recycling', doorX: 220, doorY: 3700, streetSide: 'north', district: 'Industrie', gable: 'tuitgevel', brickColor: '#15803d', trimColor: '#f0fdf4', awning: null, windowCols: 3, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 520,  y: 3700, w: 260, h: 240, color: '#f1f5f9', roof: '#020617', label: 'Machinefabriek Hollandia', doorX: 650, doorY: 3700, streetSide: 'north', district: 'Industrie', gable: 'tuitgevel', brickColor: '#0f172a', trimColor: '#94a3b8', awning: null, windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 820,  y: 3700, w: 250, h: 240, color: '#fef3c7', roof: '#451a03', label: 'Houthandel De Stam', doorX: 940, doorY: 3700, streetSide: 'north', district: 'Industrie', gable: 'trapgevel', brickColor: '#78350f', trimColor: '#fde68a', awning: null, windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: true },
      { x: 1340, y: 3700, w: 280, h: 240, color: '#e2e8f0', roof: '#1e293b', label: 'Stedelijke Energiecentrale', doorX: 1480, doorY: 3700, streetSide: 'north', district: 'Industrie', gable: 'herenhuis', brickColor: '#334155', trimColor: '#e2e8f0', awning: null, windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 2100, y: 3700, w: 260, h: 240, color: '#fff1f2', roof: '#881337', label: 'Chocoladefabriek Bonbon', doorX: 2230, doorY: 3700, streetSide: 'north', district: 'Industrie', gable: 'trapgevel', brickColor: '#be123c', trimColor: '#ffe4e6', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 520,  y: 4420, w: 260, h: 240, color: '#fef08a', roof: '#713f12', label: 'Rijschool Het Snelle Wiel', doorX: 650, doorY: 4420, streetSide: 'north', district: 'Industrie', gable: 'tuitgevel', brickColor: '#ca8a04', trimColor: '#fef9c3', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 1340, y: 4420, w: 280, h: 240, color: '#ffedd5', roof: '#7c2d12', label: "Kinderboerderij 't Weitje", doorX: 1480, doorY: 4420, streetSide: 'north', district: 'Industrie', gable: 'klokgevel', brickColor: '#9a3412', trimColor: '#fed7aa', awning: 'green_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },

      // --- SECTOR 5: UNIVERSITEIT & ZUIDERKWARTIER ---
      { x: 2900, y: 2900, w: 280, h: 240, color: '#e0f2fe', roof: '#0369a1', label: 'Universiteitsbibliotheek', doorX: 3040, doorY: 2900, streetSide: 'north', district: 'Campus Zuid', gable: 'herenhuis', brickColor: '#0284c7', trimColor: '#f0f9ff', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 3700, y: 2900, w: 260, h: 240, color: '#f3e8ff', roof: '#581c87', label: 'Faculteit Bètatechniek', doorX: 3830, doorY: 2900, streetSide: 'north', district: 'Campus Zuid', gable: 'herenhuis', brickColor: '#7e22ce', trimColor: '#faf5ff', awning: null, windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: false },
      { x: 4420, y: 2900, w: 250, h: 240, color: '#ffedd5', roof: '#7c2d12', label: 'Studentencomplex De Burcht', doorX: 4545, doorY: 2900, streetSide: 'north', district: 'Campus Zuid', gable: 'trapgevel', brickColor: '#ea580c', trimColor: '#fed7aa', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 2900, y: 3700, w: 280, h: 250, color: '#dcfce7', roof: '#14532d', label: 'Olympisch Sportcentrum', doorX: 3040, doorY: 3700, streetSide: 'north', district: 'Campus Zuid', gable: 'herenhuis', brickColor: '#16a34a', trimColor: '#f0fdf4', awning: 'green_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: false },
      { x: 3700, y: 3700, w: 260, h: 250, color: '#ffe4e6', roof: '#881337', label: 'Bioscoop Krokant Cinema', doorX: 3830, doorY: 3700, streetSide: 'north', district: 'Campus Zuid', gable: 'trapgevel', brickColor: '#e11d48', trimColor: '#ffe4e6', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 4420, y: 3700, w: 240, h: 250, color: '#fef08a', roof: '#713f12', label: 'Pizzeria Da Luigi', doorX: 4540, doorY: 3700, streetSide: 'north', district: 'Campus Zuid', gable: 'klokgevel', brickColor: '#b45309', trimColor: '#fef9c3', awning: 'green_white', windowCols: 3, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 2900, y: 4420, w: 280, h: 240, color: '#d1fae5', roof: '#064e3b', label: 'Botanische Tuinen Paviljoen', doorX: 3040, doorY: 4420, streetSide: 'north', district: 'Campus Zuid', gable: 'klokgevel', brickColor: '#059669', trimColor: '#d1fae5', awning: 'green_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true },
      { x: 3700, y: 4420, w: 260, h: 240, color: '#e2e8f0', roof: '#020617', label: 'Observatorium Sterrenwacht', doorX: 3830, doorY: 4420, streetSide: 'north', district: 'Campus Zuid', gable: 'herenhuis', brickColor: '#0f172a', trimColor: '#e2e8f0', awning: 'blue_white', windowCols: 4, windowRows: 3, flowerBoxes: false, chimney: true },
      { x: 4420, y: 4420, w: 250, h: 240, color: '#fef3c7', roof: '#78350f', label: 'Muziekcafé De Gouden Harp', doorX: 4545, doorY: 4420, streetSide: 'north', district: 'Campus Zuid', gable: 'trapgevel', brickColor: '#d97706', trimColor: '#fef3c7', awning: 'orange_white', windowCols: 4, windowRows: 3, flowerBoxes: true, chimney: true }
    ];

    // --- STADSANKLEDING: BOMEN, PARK- EN GRACHTBANKJES, FIETSENREKKEN & BLOEMENBAKKEN ---
    // 1. Authentieke Nederlandse Stadsbomen (Eiken, Lindes, Treurwilgen langs kades & terrassen)
    this.trees = [
      // Rondom Bakkerij & Centrum Dorpskern
      { x: 1040, y: 880, r: 26, type: 'oak', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1040, y: 960, r: 28, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1040, y: 1040, r: 25, type: 'cherry', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1260, y: 880, r: 27, type: 'oak', color: '#14532d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1560, y: 880, r: 26, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1840, y: 880, r: 29, type: 'oak', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      // Langs Hoofdstraat Promenade (Noord en Zuid)
      { x: 460, y: 1060, r: 24, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 680, y: 1060, r: 26, type: 'oak', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1400, y: 1060, r: 25, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1720, y: 1060, r: 28, type: 'oak', color: '#14532d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2100, y: 1060, r: 26, type: 'cherry', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 460, y: 1340, r: 25, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 780, y: 1340, r: 27, type: 'oak', color: '#14532d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1280, y: 1340, r: 26, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1600, y: 1340, r: 28, type: 'oak', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 1960, y: 1340, r: 25, type: 'cherry', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      // Langs de Prinsengracht (Grachtkades)
      { x: 2390, y: 200, r: 28, type: 'willow', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2390, y: 500, r: 26, type: 'linden', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2390, y: 800, r: 29, type: 'willow', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2390, y: 1500, r: 27, type: 'linden', color: '#14532d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2390, y: 1800, r: 28, type: 'willow', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2390, y: 2200, r: 26, type: 'linden', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2570, y: 200, r: 26, type: 'willow', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2570, y: 500, r: 28, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2570, y: 800, r: 27, type: 'willow', color: '#14532d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2570, y: 1500, r: 28, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2570, y: 1800, r: 26, type: 'willow', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2570, y: 2200, r: 29, type: 'linden', color: '#14532d', shadowColor: 'rgba(0,0,0,0.18)' },
      // Cultuurwijk & Universiteitscampus
      { x: 2820, y: 1280, r: 28, type: 'oak', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 3620, y: 1280, r: 27, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 4340, y: 1280, r: 26, type: 'oak', color: '#14532d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2820, y: 2850, r: 28, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 3620, y: 2850, r: 29, type: 'oak', color: '#15803d', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 2820, y: 3650, r: 27, type: 'linden', color: '#16a34a', shadowColor: 'rgba(0,0,0,0.18)' },
      { x: 3620, y: 3650, r: 28, type: 'oak', color: '#14532d', shadowColor: 'rgba(0,0,0,0.18)' }
    ];

    // 2. Klassieke Groen/Houten Park- en Grachtbankjes
    this.benches = [
      { x: 1040, y: 920, angle: Math.PI / 2, w: 28, h: 10 },
      { x: 1040, y: 1000, angle: Math.PI / 2, w: 28, h: 10 },
      { x: 1510, y: 1060, angle: 0, w: 32, h: 10 },
      { x: 1780, y: 1060, angle: 0, w: 32, h: 10 },
      { x: 600, y: 1060, angle: 0, w: 30, h: 10 },
      { x: 600, y: 1340, angle: 0, w: 30, h: 10 },
      { x: 1400, y: 1340, angle: 0, w: 32, h: 10 },
      { x: 1800, y: 1340, angle: 0, w: 32, h: 10 },
      // Langs de Prinsengracht kades
      { x: 2390, y: 350, angle: Math.PI / 2, w: 30, h: 10 },
      { x: 2390, y: 650, angle: Math.PI / 2, w: 30, h: 10 },
      { x: 2390, y: 1650, angle: Math.PI / 2, w: 30, h: 10 },
      { x: 2390, y: 2000, angle: Math.PI / 2, w: 30, h: 10 },
      { x: 2570, y: 350, angle: -Math.PI / 2, w: 30, h: 10 },
      { x: 2570, y: 650, angle: -Math.PI / 2, w: 30, h: 10 },
      { x: 2570, y: 1650, angle: -Math.PI / 2, w: 30, h: 10 },
      { x: 2570, y: 2000, angle: -Math.PI / 2, w: 30, h: 10 },
      // Cultuurwijk & Haven
      { x: 3200, y: 340, angle: 0, w: 32, h: 10 },
      { x: 4000, y: 340, angle: 0, w: 32, h: 10 },
      { x: 3200, y: 1280, angle: 0, w: 32, h: 10 },
      { x: 4000, y: 1280, angle: 0, w: 32, h: 10 },
      { x: 3200, y: 2850, angle: 0, w: 32, h: 10 },
      { x: 4000, y: 2850, angle: 0, w: 32, h: 10 }
    ];

    // 3. Typisch Nederlandse Fietsenrekken met Geparkeerde Omafietsen
    this.bikeRacks = [
      { x: 1270, y: 940, angle: 0, count: 4, bikes: [{ color: '#dc2626', basket: true }, { color: '#2563eb', basket: false }, { color: '#059669', basket: true }, { color: '#d97706', basket: false }] },
      { x: 1540, y: 1060, angle: 0, count: 3, bikes: [{ color: '#7c3aed', basket: true }, { color: '#09090b', basket: true }, { color: '#ea580c', basket: false }] },
      { x: 890, y: 340, angle: 0, count: 3, bikes: [{ color: '#0284c7', basket: false }, { color: '#16a34a', basket: true }, { color: '#475569', basket: false }] },
      { x: 1440, y: 340, angle: 0, count: 4, bikes: [{ color: '#ec4899', basket: true }, { color: '#09090b', basket: false }, { color: '#059669', basket: true }, { color: '#2563eb', basket: false }] },
      { x: 2850, y: 1280, angle: 0, count: 5, bikes: [{ color: '#dc2626', basket: true }, { color: '#2563eb', basket: false }, { color: '#16a34a', basket: true }, { color: '#eab308', basket: false }, { color: '#7c3aed', basket: true }] },
      { x: 2850, y: 2850, angle: 0, count: 5, bikes: [{ color: '#0284c7', basket: true }, { color: '#ea580c', basket: false }, { color: '#059669', basket: true }, { color: '#18181b', basket: false }, { color: '#ec4899', basket: true }] },
      { x: 3650, y: 2850, angle: 0, count: 4, bikes: [{ color: '#2563eb', basket: false }, { color: '#dc2626', basket: true }, { color: '#475569', basket: false }, { color: '#059669', basket: true }] }
    ];

    // 4. Kleurrijke Bloembakken & Terracotta Plantenpotten (Geraniums & Tulpen)
    this.planters = [
      { x: 1260, y: 1010, type: 'terracotta', flowerColor: '#ef4444' },
      { x: 1260, y: 1050, type: 'terracotta', flowerColor: '#f59e0b' },
      { x: 1610, y: 1060, type: 'box', flowerColor: '#ec4899', w: 22, h: 8 },
      { x: 1690, y: 1060, type: 'box', flowerColor: '#eab308', w: 22, h: 8 },
      { x: 1440, y: 1340, type: 'box', flowerColor: '#ef4444', w: 22, h: 8 },
      { x: 1520, y: 1340, type: 'box', flowerColor: '#3b82f6', w: 22, h: 8 },
      { x: 2390, y: 280, type: 'terracotta', flowerColor: '#f97316' },
      { x: 2390, y: 720, type: 'terracotta', flowerColor: '#ec4899' },
      { x: 2390, y: 1720, type: 'terracotta', flowerColor: '#ef4444' },
      { x: 2570, y: 280, type: 'terracotta', flowerColor: '#eab308' },
      { x: 2570, y: 720, type: 'terracotta', flowerColor: '#3b82f6' },
      { x: 2570, y: 1720, type: 'terracotta', flowerColor: '#10b981' }
    ];

    // --- NEDERLANDSE GRACHTEN & OPENSTAANDE OPHAALBRUG ---
    // Grachtenwaterloop met kades, meerpalen, groot binnenvaartschip en rondvaartboot
    this.canals = [
      {
        id: 'gracht_prinsen',
        name: 'De Prinsengracht',
        x: 2420,
        y: 0,
        w: 120,
        h: 2650,
        waterColor: '#0e7490',
        quayColor: '#94a3b8',
        boats: [
          // 1. Groot Binnenvaartschip "De Volharding" (Vervoert meel & graan voor Bakkerij Knus & Krokant)
          {
            id: 'ship_cargo_volharding',
            name: 'Binnenvaartschip De Volharding',
            type: 'freighter',
            x: 2480,
            y: 450,
            vy: 0.95,
            targetSpeed: 0.95,
            minY: 100,
            maxY: 2450,
            w: 48,
            h: 148,
            angle: Math.PI / 2,
            hullColor: '#1e293b',
            deckColor: '#334155',
            cargoColor: '#ca8a04',
            cabinColor: '#f8fafc',
            flagColor: '#ef4444',
            hornCooldown: 0,
            isWaiting: false,
            wakeParticles: []
          },
          // 2. Klassieke Rondvaartboot "De Krokante Zwaan" met glazen panoramadak
          {
            id: 'ship_tour_zwaan',
            name: 'De Krokante Zwaan',
            type: 'tour',
            x: 2480,
            y: 1950,
            vy: -0.85,
            targetSpeed: 0.85,
            minY: 120,
            maxY: 2400,
            w: 42,
            h: 92,
            angle: -Math.PI / 2,
            color: '#ffffff',
            roofColor: '#1e3a8a',
            flagColor: '#ef4444',
            hornCooldown: 0,
            isWaiting: false,
            wakeParticles: []
          }
        ]
      }
    ];
    // Backward compatibility pointer
    this.canals[0].boat = this.canals[0].boats[0];

    // Klassieke Hollandse Witte Ophaalbrug "De Eendracht" over de Prinsengracht (Hoofdstraat)
    // Gaat daadwerkelijk open en dicht zodra grote schepen naderen!
    this.drawBridges = [
      {
        id: 'ophaalbrug_hoofdstraat',
        name: 'Ophaalbrug De Eendracht',
        roadName: 'Hoofdstraat',
        x: 2360,
        y: 1100,
        w: 240,
        h: 200,
        deckX: 2420,
        deckY: 1100,
        deckW: 120,
        deckH: 200,
        state: 'closed', // 'closed', 'warning', 'opening', 'open', 'closing'
        timer: 0,
        openProgress: 0.0, // 0.0 (dicht) tot 1.0 (volledig geopend)
        barrierLowered: false,
        warningBellTimer: 0,
        activeShip: null
      }
    ];

    // --- SPORTIEVE WIELRENNERS & BAKFIETSMOEDERS ---
    this.cyclists = [
      // 1. Wielrennerspeloton (4 renners in strakke waaier-formatie)
      {
        id: 'peloton_lead',
        type: 'peloton',
        role: 'leader',
        name: 'Ploegleider Thijs (Geel)',
        x: 800,
        y: 1085,
        angle: 0,
        speed: 3.8,
        targetSpeed: 3.8,
        jerseyColor: '#eab308',
        helmetColor: '#000000',
        bellCooldown: 0,
        pedalPhase: 0,
        waypointIdx: 0,
        waypoints: [
          { x: 1800, y: 1085 }, { x: 2300, y: 1085 }, { x: 2600, y: 1085 },
          { x: 2665, y: 1150 }, { x: 2665, y: 2600 }, { x: 2600, y: 2665 },
          { x: 1250, y: 2665 }, { x: 1185, y: 2600 }, { x: 1185, y: 1150 },
          { x: 1250, y: 1085 }, { x: 700, y: 1085 }
        ]
      },
      {
        id: 'peloton_2',
        type: 'peloton',
        role: 'follower',
        name: 'Wielrenner Bram (Cyan)',
        x: 735,
        y: 1085,
        angle: 0,
        speed: 3.8,
        targetSpeed: 3.8,
        jerseyColor: '#06b6d4',
        helmetColor: '#0f172a',
        bellCooldown: 0,
        pedalPhase: 0.35
      },
      {
        id: 'peloton_3',
        type: 'peloton',
        role: 'follower',
        name: 'Wielrenner Daan (Roze)',
        x: 670,
        y: 1085,
        angle: 0,
        speed: 3.8,
        targetSpeed: 3.8,
        jerseyColor: '#ec4899',
        helmetColor: '#18181b',
        bellCooldown: 0,
        pedalPhase: 0.7
      },
      {
        id: 'peloton_4',
        type: 'peloton',
        role: 'follower',
        name: 'Wielrenner Lars (Oranje)',
        x: 605,
        y: 1085,
        angle: 0,
        speed: 3.8,
        targetSpeed: 3.8,
        jerseyColor: '#f97316',
        helmetColor: '#18181b',
        bellCooldown: 0,
        pedalPhase: 1.05
      },
      // 2. Bakfietsmoeders (Hollandse tweewieler bakfiets met houten kuip & vlaggetje)
      {
        id: 'bakfiets_1',
        type: 'bakfiets',
        name: 'Mevrouw Berendsen (Baguettes & Tulpen)',
        x: 950,
        y: 335,
        angle: Math.PI,
        speed: 1.6,
        targetSpeed: 1.6,
        boxColor: '#b45309',
        frameColor: '#0284c7',
        bellCooldown: 0,
        pedalPhase: 0,
        waypointIdx: 0,
        waypoints: [
          { x: 380, y: 335 }, { x: 370, y: 400 }, { x: 370, y: 1050 },
          { x: 420, y: 1080 }, { x: 1050, y: 1080 }, { x: 1080, y: 1020 },
          { x: 1080, y: 400 }, { x: 1050, y: 335 }
        ]
      },
      {
        id: 'bakfiets_2',
        type: 'bakfiets',
        name: 'Bloemist Fleur (Verse Bloemen)',
        x: 1550,
        y: 1335,
        angle: 0,
        speed: 1.5,
        targetSpeed: 1.5,
        boxColor: '#78350f',
        frameColor: '#16a34a',
        bellCooldown: 0,
        pedalPhase: 0.5,
        waypointIdx: 0,
        waypoints: [
          { x: 2150, y: 1335 }, { x: 2160, y: 1400 }, { x: 2160, y: 1820 },
          { x: 1800, y: 1820 }, { x: 1300, y: 1820 }, { x: 1250, y: 1750 },
          { x: 1250, y: 1335 }
        ]
      }
    ];

    // Delivery stations / objectives (positioned conveniently along the curbs/streets)
    this.destinations = [
      { id: 'hq', name: 'Knus & Krokant (Laadzone)', x: 1270, y: 1055, r: 45, type: 'bakery', districtId: 'dorpskern' },
      { id: 'jansen', name: 'Familie Jansen (Croissants)', x: 640, y: 370, r: 40, type: 'house', districtId: 'dorpskern' },
      { id: 'horizon', name: 'Kantoor Horizon (Lunchbox)', x: 930, y: 370, r: 40, type: 'office', districtId: 'dorpskern' },
      { id: 'rozenhof', name: 'Villa Rozenhof (Appeltaart)', x: 1480, y: 370, r: 40, type: 'mansion', districtId: 'dorpskern' },
      { id: 'vandijk', name: 'Meneer van Dijk (Koffie)', x: 940, y: 1280, r: 40, type: 'house', districtId: 'campus' },
      { id: 'student', name: 'Studentenflat (Pizzabroodjes)', x: 220, y: 1280, r: 40, type: 'house', districtId: 'campus' },
      { id: 'tandarts', name: 'Tandartspraktijk Wit (Krentenbollen)', x: 1480, y: 1280, r: 40, type: 'office', districtId: 'campus' },
      { id: 'bloem', name: 'Atelier Bloem (Luxe Petit Fours)', x: 1740, y: 1280, r: 40, type: 'shop', districtId: 'centrum' },
      { id: 'anker', name: 'Grand Café Het Anker (Catering Taart)', x: 1650, y: 1055, r: 40, type: 'cafe', districtId: 'centrum' },
      { id: 'oost', name: 'Notaris Oost (Saucijzenbrood)', x: 2150, y: 1280, r: 40, type: 'office', districtId: 'centrum' },

      // Nieuwe Bestemmingen verspreid over de 4x grotere stad
      { id: 'haven', name: 'Pakhuizen De Zwaan (Roggebrood)', x: 2230, y: 870, r: 40, type: 'shop', districtId: 'haven' },
      { id: 'visafslag', name: 'Visafslag De Zeelt (Saucijzen)', x: 3030, y: 370, r: 40, type: 'shop', districtId: 'haven' },
      { id: 'vuurtoren', name: 'Vuurtoren Havenlicht (Koffiebroodjes)', x: 3820, y: 370, r: 40, type: 'office', districtId: 'haven' },
      { id: 'zeewind', name: 'Brasserie Zeewind (Stokbrood)', x: 3820, y: 870, r: 40, type: 'cafe', districtId: 'haven' },
      { id: 'theater', name: 'Stadsschouwburg (Theatercatering)', x: 3040, y: 1280, r: 40, type: 'office', districtId: 'oost' },
      { id: 'concert', name: 'Concertgebouw De Gouden Noot', x: 3830, y: 1280, r: 40, type: 'cafe', districtId: 'oost' },
      { id: 'hotel', name: 'Grand Hotel Majestic (Luxe Ontbijt)', x: 2230, y: 2050, r: 40, type: 'mansion', districtId: 'oost' },
      { id: 'wellness', name: 'Wellness & Spa De Bron (Muffins)', x: 3830, y: 2050, r: 40, type: 'shop', districtId: 'oost' },
      { id: 'brandweer', name: 'Brandweerkazerne Zuid (Taart)', x: 650, y: 2050, r: 40, type: 'office', districtId: 'industrie' },
      { id: 'meubel', name: 'Meubelfabriek De Eik (Lunchbox)', x: 220, y: 2050, r: 40, type: 'office', districtId: 'industrie' },
      { id: 'brouwerij', name: 'Brouwerij Gouden Hop (Borrelbrood)', x: 220, y: 2850, r: 40, type: 'cafe', districtId: 'industrie' },
      { id: 'garage', name: 'Autogarage Van Dam & Zn. (Snack)', x: 940, y: 2850, r: 40, type: 'shop', districtId: 'industrie' },
      { id: 'bieb', name: 'Universiteitsbibliotheek (Studeerbox)', x: 3040, y: 2850, r: 40, type: 'office', districtId: 'zuid' },
      { id: 'sport', name: 'Olympisch Sportcentrum (Proteïnebrood)', x: 3040, y: 3650, r: 40, type: 'office', districtId: 'zuid' },
      { id: 'cinema', name: 'Bioscoop Krokant Cinema (Taart)', x: 3830, y: 3650, r: 40, type: 'cafe', districtId: 'zuid' },
      { id: 'luigi', name: 'Pizzeria Da Luigi (Deeg & Focaccia)', x: 4540, y: 3650, r: 40, type: 'cafe', districtId: 'zuid' },
      { id: 'boerderij', name: "Kinderboerderij 't Weitje (Appelbollen)", x: 1480, y: 4370, r: 40, type: 'house', districtId: 'industrie' }
    ];

    // Dynamic Obstacles & Stadsconcurrentie
    // 1. Actieve Stadsconcurrent: Knus Expres Bestelbus (bestuurd door Sjors de Snelle)
    // Rijdt uitsluitend over het openbare asfaltwegennetwerk en kan fysiek NOOIT door gebouwen heen
    this.van = {
      x: 1400,
      y: 1250,
      w: 96,
      h: 46,
      angle: 0,
      targetAngle: 0,
      speed: 0,
      targetSpeed: 2.2,
      state: 'driving', // 'driving' of 'delivering'
      stateTimer: 0,
      waypointIndex: 0,
      waypoints: [
        // 1. Hoofdstraat (Oostwaarts naar Tandarts, Bloem & Notaris)
        { x: 1480, y: 1250, stop: true, duration: 4.8, destId: 'tandarts', name: 'Tandartspraktijk Wit', address: 'Hoofdstraat 52', product: 'Zachte Krentenbollen', doorX: 1480, doorY: 1330 },
        { x: 1740, y: 1250, stop: true, duration: 4.8, destId: 'bloem', name: 'Atelier Bloem', address: 'Hoofdstraat 78', product: 'Luxe Petit Fours', doorX: 1740, doorY: 1330 },
        { x: 1950, y: 1250, stop: false, name: 'Kruising Hoofdstraat - Oostwal' },
        { x: 2150, y: 1250, stop: true, duration: 4.8, destId: 'oost', name: 'Notariskantoor Oost', address: 'Hoofdstraat 112', product: 'Warme Saucijzenbroodjes', doorX: 2150, doorY: 1330 },
        { x: 2280, y: 1250, stop: false, name: 'Hoofdstraat Keerlus Zuid' },
        { x: 2320, y: 1200, stop: false, name: 'Hoofdstraat Keerlus Oost' },
        { x: 2280, y: 1150, stop: false, name: 'Hoofdstraat Keerlus Noord' },
        // 2. Hoofdstraat (Westwaarts langs terras Grand Café Het Anker)
        { x: 1950, y: 1150, stop: false, name: 'Hoofdstraat Westwaarts' },
        { x: 1650, y: 1150, stop: true, duration: 4.8, destId: 'anker', name: 'Grand Café Het Anker', address: 'Hoofdstraat 14 (Terras)', product: 'Luxe Slagroomtaart', doorX: 1650, doorY: 1080 },
        { x: 1850, y: 1150, stop: false, name: 'Aansluiting Oostwal' },
        { x: 1950, y: 1150, stop: false, name: 'Draai naar Oostwal' },
        // 3. Oostwal (Noordwaarts naar Noorderlaan)
        { x: 1950, y: 800,  stop: false, name: 'Oostwal Gracht' },
        { x: 1950, y: 420,  stop: false, name: 'Kruising Noorderlaan' },
        // 4. Noorderlaan (Westwaarts langs Villa Rozenhof, Horizon & Jansen)
        { x: 1800, y: 390,  stop: false, name: 'Noorderlaan Villawijk' },
        { x: 1480, y: 390,  stop: true, duration: 4.8, destId: 'rozenhof', name: 'Villa Rozenhof (Nr. 42)', address: 'Noorderlaan 42', product: 'Ambachtelijke Appeltaart', doorX: 1480, doorY: 320 },
        { x: 1200, y: 390,  stop: false, name: 'Kruising Bakkerijboulevard Noord' },
        { x: 930,  y: 390,  stop: true, duration: 4.8, destId: 'horizon', name: 'Kantoor Horizon (Nr. 18)', address: 'Noorderlaan 18', product: 'Zakelijke Lunchboxen', doorX: 930, doorY: 320 },
        { x: 640,  y: 390,  stop: true, duration: 4.8, destId: 'jansen', name: 'Familie Jansen (Nr. 4)', address: 'Noorderlaan 4', product: 'Verse Warme Croissants', doorX: 640, doorY: 320 },
        { x: 420,  y: 390,  stop: false, name: 'Kruising Klinkerstraat' },
        // 5. Klinkerstraat (Zuidwaarts langs Studentenflat Campus)
        { x: 390,  y: 700,  stop: false, name: 'Klinkerstraat Noord' },
        { x: 390,  y: 1100, stop: false, name: 'Klinkerstraat Midden' },
        { x: 390,  y: 1420, stop: true, duration: 4.8, destId: 'student', name: 'Studentenflat Campus', address: 'Klinkerstraat 5', product: 'Warme Kaas- & Pizzabroodjes', doorX: 320, doorY: 1420 },
        { x: 390,  y: 1800, stop: false, name: 'Klinkerstraat Zuid' },
        { x: 420,  y: 1950, stop: false, name: 'Kruising Singelweg' },
        // 6. Singelweg (Oostwaarts langs Singelpark)
        { x: 800,  y: 1950, stop: false, name: 'Singelpark West' },
        { x: 1200, y: 1950, stop: false, name: 'Singelweg Fontein' },
        { x: 1600, y: 1950, stop: false, name: 'Singelpark Oost' },
        { x: 1920, y: 1950, stop: false, name: 'Kruising Singelweg - Oostwal' },
        // 7. Oostwal (Noordwaarts terug naar Hoofdstraat)
        { x: 1950, y: 1600, stop: false, name: 'Oostwal Zuidwaarts' },
        { x: 1950, y: 1250, stop: false, name: 'Kruising Hoofdstraat Oost' },
        // 8. Hoofdstraat (Westwaarts langs Meneer van Dijk en Rivaal Depot)
        { x: 1600, y: 1250, stop: false, name: 'Hoofdstraat Midden' },
        { x: 1200, y: 1250, stop: false, name: 'Kruising Bakkerijboulevard' },
        { x: 940,  y: 1250, stop: true, duration: 4.8, destId: 'vandijk', name: 'Meneer van Dijk (Nr. 88)', address: 'Hoofdstraat 88', product: 'Filterkoffie & Boterkoek', doorX: 940, doorY: 1330 },
        { x: 650,  y: 1250, stop: true, duration: 4.8, destId: 'depot', name: 'Knus Expres Bakkerij & Depot', address: 'Hoofdstraat 24', product: 'Nieuwe Voorraad Baksels Inladen', doorX: 650, doorY: 1330 },
        { x: 420,  y: 1250, stop: false, name: 'Keerlus West Zuid' },
        { x: 380,  y: 1200, stop: false, name: 'Keerlus West Bocht' },
        { x: 420,  y: 1150, stop: false, name: 'Keerlus West Noord' },
        { x: 650,  y: 1150, stop: false, name: 'Hoofdstraat Westbaans' },
        { x: 1000, y: 1250, stop: false, name: 'Aanloop Centrum Oostwaarts' }
      ],
      currentStop: 'Tandartspraktijk Wit',
      deliveries: 1, // Knus Expres begint al met 1 levering
      doorOpen: false,
      doorTimer: 0,
      hazardBlink: false,
      hazardTimer: 0,
      honkCooldown: 0,
      courierVisible: false,
      courierX: 0,
      courierY: 0,
      courierAngle: 0,
      courierWalkCycle: 0,
      courierHasBox: true,
      packageDelivered: false,
      targetDoorX: 1480,
      targetDoorY: 1330,
      currentAddress: 'Hoofdstraat 52',
      currentProduct: 'Zachte Krentenbollen',
      lapCount: 0,
      activeStopIds: ['tandarts', 'anker', 'rozenhof', 'student', 'oost'],
      speechText: t('chat.rival'),
      speechTimer: 5.0,
      smokeParticles: [],
      label: 'Knus Expres Stadsconcurrent'
    };

    // 2. Road works barriers, excavation gravel trenches & bouncing traffic cones
    this.roadWorks = [
      {
        id: 'rw_centrum',
        x: 960,
        y: 355,
        w: 95,
        h: 22,
        label: 'Wegwerkzaamheden Noorderlaan',
        dir: 'left',
        sand: { x: 950, y: 345, w: 115, h: 42 },
      },
      {
        id: 'rw_haven',
        x: 420,
        y: 1350,
        w: 24,
        h: 90,
        label: 'Wegwerkzaamheden Industrieweg',
        dir: 'right',
        sand: { x: 410, y: 1340, w: 44, h: 110 },
              },
      {
        id: 'rw_campus',
        x: 1850,
        y: 1140,
        w: 95,
        h: 22,
        label: 'Wegwerkzaamheden Campuslaan',
        dir: 'left',
        sand: { x: 1840, y: 1130, w: 115, h: 42 },
      },
      {
        id: 'rw_zuid',
        x: 1160,
        y: 1820,
        w: 24,
        h: 95,
        label: 'Wegwerkzaamheden Zuidring',
        dir: 'left',
        sand: { x: 1150, y: 1810, w: 44, h: 115 },
      }
    ];
    // Backward compatibility for direct property access
    Object.assign(this.roadWorks, this.roadWorks[0]);

    // Bouncing traffic cones surrounding the roadworks
    this.cones = [
      // Noorderlaan
      { id: 1, x: 935, y: 380, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 2, x: 970, y: 405, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 3, x: 1010, y: 415, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 4, x: 1050, y: 405, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 5, x: 1085, y: 380, r: 9, vx: 0, vy: 0, knocked: false },
      // Haven / Industrieweg
      { id: 6, x: 452, y: 1330, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 7, x: 468, y: 1370, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 8, x: 468, y: 1410, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 9, x: 452, y: 1450, r: 9, vx: 0, vy: 0, knocked: false },
      // Campuslaan
      { id: 10, x: 1825, y: 1165, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 11, x: 1865, y: 1190, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 12, x: 1910, y: 1195, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 13, x: 1950, y: 1170, r: 9, vx: 0, vy: 0, knocked: false },
      // Zuidring
      { id: 14, x: 1192, y: 1800, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 15, x: 1208, y: 1845, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 16, x: 1208, y: 1885, r: 9, vx: 0, vy: 0, knocked: false },
      { id: 17, x: 1192, y: 1925, r: 9, vx: 0, vy: 0, knocked: false }
    ];

    // 2c. Dynamische Tijdelijke Files & Opstoppingen (Traffic Jams met Echte Auto's)
    this.trafficJams = [
      {
        id: 'jam_hoofdstraat',
        name: 'Hoofdstraat Winkelhart',
        x: 690,
        y: 1115,
        w: 430,
        h: 70,
        axis: 'horizontal',
        active: true,
        timer: 35.0,
        cycleInterval: 65.0,
        slalomCooldown: 0,
        label: '🚗🚗 FILE: Hoofdstraat',
        cars: [
          { id: 'jh_1', name: 'Blauwe Station', x: 740, y: 1150, angle: Math.PI, length: 72, width: 34, model: 'station', color: '#1e3a8a', roofColor: '#172554', isBraking: true, speed: 0, exhaustTimer: 0 },
          { id: 'jh_2', name: 'Rode Golf', x: 825, y: 1148, angle: Math.PI + 0.02, length: 64, width: 33, model: 'hatchback', color: '#dc2626', roofColor: '#991b1b', isBraking: true, speed: 0, exhaustTimer: 0.3 },
          { id: 'jh_3', name: 'Grijze Sedan', x: 905, y: 1152, angle: Math.PI - 0.015, length: 70, width: 34, model: 'sedan', color: '#475569', roofColor: '#1e293b', isBraking: true, speed: 0, exhaustTimer: 0.6 },
          { id: 'jh_4', name: 'Amsterdamse Taxi', x: 988, y: 1149, angle: Math.PI + 0.01, length: 66, width: 34, model: 'sedan', color: '#eab308', roofColor: '#09090b', isBraking: true, speed: 0, exhaustTimer: 0.9 },
          { id: 'jh_5', name: 'Groene Bestelbus', x: 1070, y: 1151, angle: Math.PI, length: 78, width: 36, model: 'van', color: '#047857', roofColor: '#064e3b', isBraking: true, speed: 0, exhaustTimer: 0.4 }
        ]
      },
      {
        id: 'jam_klinker',
        name: 'Klinkerstraat Spits',
        x: 395,
        y: 640,
        w: 55,
        h: 330,
        axis: 'vertical',
        active: false,
        timer: 0,
        cycleInterval: 75.0,
        slalomCooldown: 0,
        label: '🚗🚗 FILE: Klinkerstraat',
        cars: [
          { id: 'jk_1', name: 'Zilveren Sedan', x: 420, y: 690, angle: Math.PI / 2, length: 68, width: 34, model: 'sedan', color: '#94a3b8', roofColor: '#475569', isBraking: true, speed: 0, exhaustTimer: 0 },
          { id: 'jk_2', name: 'Witte Bestelbus', x: 422, y: 770, angle: Math.PI / 2 - 0.02, length: 72, width: 35, model: 'van', color: '#f8fafc', roofColor: '#334155', isBraking: true, speed: 0, exhaustTimer: 0.35 },
          { id: 'jk_3', name: 'Oranje Cabrio', x: 418, y: 850, angle: Math.PI / 2 + 0.015, length: 62, width: 33, model: 'cabrio', color: '#ea580c', roofColor: '#9a3412', isBraking: true, speed: 0, exhaustTimer: 0.7 },
          { id: 'jk_4', name: 'Zwarte SUV', x: 420, y: 928, angle: Math.PI / 2, length: 72, width: 36, model: 'station', color: '#18181b', roofColor: '#09090b', isBraking: true, speed: 0, exhaustTimer: 0.5 }
        ]
      },
      {
        id: 'jam_oostwal',
        name: 'Oostwal Ring',
        x: 1890,
        y: 640,
        w: 60,
        h: 330,
        axis: 'vertical',
        active: false,
        timer: 0,
        cycleInterval: 80.0,
        slalomCooldown: 0,
        label: '🚗🚗 FILE: Oostwal Ring',
        cars: [
          { id: 'jo_1', name: 'Bordeaux Station', x: 1920, y: 690, angle: -Math.PI / 2, length: 72, width: 34, model: 'station', color: '#881337', roofColor: '#4c0519', isBraking: true, speed: 0, exhaustTimer: 0 },
          { id: 'jo_2', name: 'Blauwe Hatchback', x: 1918, y: 770, angle: -Math.PI / 2 + 0.02, length: 62, width: 32, model: 'hatchback', color: '#2563eb', roofColor: '#1d4ed8', isBraking: true, speed: 0, exhaustTimer: 0.4 },
          { id: 'jo_3', name: 'Gele Pakketbus', x: 1922, y: 850, angle: -Math.PI / 2 - 0.015, length: 76, width: 36, model: 'van', color: '#f59e0b', roofColor: '#b45309', isBraking: true, speed: 0, exhaustTimer: 0.8 },
          { id: 'jo_4', name: 'Donkergrijze Sedan', x: 1920, y: 928, angle: -Math.PI / 2, length: 68, width: 34, model: 'sedan', color: '#64748b', roofColor: '#334155', isBraking: true, speed: 0, exhaustTimer: 0.2 }
        ]
      }
    ];

    // 3. Waypoints specifiek voor katten: Stoepen (Sidewalks) & Oversteekpunten (Crosswalks) door de hele stad
    this.sidewalkWaypoints = [
      // Bakkerij HQ & Terras Stoepen (Centrum West)
      { x: 1040, y: 820, type: 'sidewalk', desc: 'Bakkerij Terras Noord' },
      { x: 1040, y: 1080, type: 'sidewalk', desc: 'Bakkerij Stoep Ingang' },
      { x: 920, y: 1080, type: 'sidewalk', desc: 'Bakkerij Voorgevel Trottoir' },
      { x: 1260, y: 820, type: 'sidewalk', desc: 'Bakkerij Laadzone Stoep' },
      { x: 1260, y: 1080, type: 'sidewalk', desc: 'Bakkerij Oostelijke Stoep' },
      { x: 800, y: 1080, type: 'sidewalk', desc: 'Bakkerijkwartier West Stoep' },
      
      // Hoofdstraat (Y ≈ 1200) - Noordelijke Stoep (Y ≈ 1120) & Zuidelijke Stoep (Y ≈ 1280)
      { x: 260, y: 1120, type: 'sidewalk', desc: 'Hoofdstraat West Noord-Stoep' },
      { x: 520, y: 1120, type: 'sidewalk', desc: 'Klinkerhoek Noord Stoep' },
      { x: 740, y: 1120, type: 'sidewalk', desc: 'Hoofdstraat Zebrapad Noord-West' },
      { x: 1060, y: 1120, type: 'sidewalk', desc: 'Hoofdstraat Bakkerij Noord-Stoep' },
      { x: 1460, y: 1120, type: 'sidewalk', desc: 'Grand Café Stoep Noord' },
      { x: 1780, y: 1120, type: 'sidewalk', desc: 'Oostwal Kruising Noord-Stoep' },
      { x: 2160, y: 1120, type: 'sidewalk', desc: 'Hoofdstraat Gracht Noord-Stoep' },
      { x: 2680, y: 1120, type: 'sidewalk', desc: 'Havenboulevard Noord-Stoep' },
      { x: 3450, y: 1120, type: 'sidewalk', desc: 'Universiteitslaan Noord-Stoep' },
      { x: 4250, y: 1120, type: 'sidewalk', desc: 'Oosterring Noord-Stoep' },

      { x: 260, y: 1280, type: 'sidewalk', desc: 'Hoofdstraat West Zuid-Stoep' },
      { x: 520, y: 1280, type: 'sidewalk', desc: 'Klinkerhoek Zuid Stoep' },
      { x: 740, y: 1280, type: 'sidewalk', desc: 'Hoofdstraat Zebrapad Zuid-West' },
      { x: 1060, y: 1280, type: 'sidewalk', desc: 'Hoofdstraat Zuid Tegenover Bakkerij' },
      { x: 1460, y: 1280, type: 'sidewalk', desc: 'Tandartspraktijk Voortuin Stoep' },
      { x: 1780, y: 1280, type: 'sidewalk', desc: 'Oostwal Kruising Zuid-Stoep' },
      { x: 2160, y: 1280, type: 'sidewalk', desc: 'Hoofdstraat Gracht Zuid-Stoep' },
      { x: 2680, y: 1280, type: 'sidewalk', desc: 'Havenboulevard Zuid-Stoep' },
      { x: 3450, y: 1280, type: 'sidewalk', desc: 'Universiteitslaan Zuid-Stoep' },
      { x: 4250, y: 1280, type: 'sidewalk', desc: 'Oosterring Zuid-Stoep' },

      // Noorderlaan (Y ≈ 420) - Noordelijke Stoep (Y ≈ 350) & Zuidelijke Stoep (Y ≈ 490)
      { x: 360, y: 350, type: 'sidewalk', desc: 'Klinkerstraat Noord Trottoir' },
      { x: 680, y: 350, type: 'sidewalk', desc: 'Noorderlaan Trottoir West' },
      { x: 1050, y: 350, type: 'sidewalk', desc: 'Noorderlaan Bakkerij Noord' },
      { x: 1520, y: 350, type: 'sidewalk', desc: 'Villa Rozenhof Gazon Stoep' },
      { x: 1880, y: 350, type: 'sidewalk', desc: 'Oostwal Noord Stoep' },
      { x: 2680, y: 350, type: 'sidewalk', desc: 'Noorderhaven Kade Stoep' },
      { x: 3450, y: 350, type: 'sidewalk', desc: 'Campus Noord Stoep' },
      { x: 4250, y: 350, type: 'sidewalk', desc: 'Oosterring Noordkade' },

      { x: 360, y: 490, type: 'sidewalk', desc: 'Noorderlaan Zuid-West Stoep' },
      { x: 680, y: 490, type: 'sidewalk', desc: 'Noorderlaan Villawijk Zuid-Stoep' },
      { x: 1050, y: 490, type: 'sidewalk', desc: 'Noorderlaan Bakkerij Zuid-Stoep' },
      { x: 1520, y: 490, type: 'sidewalk', desc: 'Grand Café Noordterras' },
      { x: 1880, y: 490, type: 'sidewalk', desc: 'Prinsengracht Noordkade' },
      { x: 2680, y: 490, type: 'sidewalk', desc: 'Havenkantoor Stoep' },

      // Singelweg & Stadspark (Y ≈ 1920) - Stoepen (Y ≈ 1850 & Y ≈ 1990)
      { x: 360, y: 1850, type: 'sidewalk', desc: 'Studentencampus Pad West' },
      { x: 760, y: 1850, type: 'sidewalk', desc: 'Singelweg Midden Stoep' },
      { x: 1200, y: 1850, type: 'sidewalk', desc: 'Boulevard Zuid Stoep' },
      { x: 1620, y: 1850, type: 'sidewalk', desc: 'Singelweg Oost Gazon' },
      { x: 1900, y: 1850, type: 'sidewalk', desc: 'Singelweg Park Wandellaan' },
      { x: 2680, y: 1850, type: 'sidewalk', desc: 'Singelhaven Kade' },
      { x: 3450, y: 1850, type: 'sidewalk', desc: 'Sportpark Singel Stoep' },
      { x: 4250, y: 1850, type: 'sidewalk', desc: 'Oosterpark Trottoir' },

      { x: 360, y: 1990, type: 'sidewalk', desc: 'Singelweg Zuid-West Stoep' },
      { x: 760, y: 1990, type: 'sidewalk', desc: 'Singelweg Parkvijver Kade' },
      { x: 1200, y: 1990, type: 'sidewalk', desc: 'Stadspark Gazon Stoep' },
      { x: 1620, y: 1990, type: 'sidewalk', desc: 'Stadspark Fonteinpad' },
      { x: 2160, y: 1990, type: 'sidewalk', desc: 'Notariskantoor Gazon Stoep' },
      { x: 2680, y: 1990, type: 'sidewalk', desc: 'Vissershaven Zuid-Stoep' },

      // Zuiderboulevard (Y ≈ 2740) - Stoepen (Y ≈ 2670 & Y ≈ 2810)
      { x: 420, y: 2670, type: 'sidewalk', desc: 'Zuiderboulevard West Noord-Stoep' },
      { x: 1200, y: 2670, type: 'sidewalk', desc: 'Winkelcentrum Zuid Noord-Stoep' },
      { x: 1920, y: 2670, type: 'sidewalk', desc: 'Prinsengracht Sluis Stoep' },
      { x: 2740, y: 2670, type: 'sidewalk', desc: 'Havenplein Noord-Stoep' },
      { x: 3520, y: 2670, type: 'sidewalk', desc: 'Campus Zuid Promenade' },
      { x: 4320, y: 2670, type: 'sidewalk', desc: 'Oosterring Zuidkade' },

      { x: 420, y: 2810, type: 'sidewalk', desc: 'Zuiderboulevard West Zuid-Stoep' },
      { x: 1200, y: 2810, type: 'sidewalk', desc: 'Winkelcentrum Zuid Zuid-Stoep' },
      { x: 1920, y: 2810, type: 'sidewalk', desc: 'Sluiswachtershuis Stoep' },
      { x: 2740, y: 2810, type: 'sidewalk', desc: 'Havenplein Zuid-Stoep' },
      { x: 3520, y: 2810, type: 'sidewalk', desc: 'Universiteitslaan Zuid-Stoep' },
      { x: 4320, y: 2810, type: 'sidewalk', desc: 'Oosterring Zuid-Stoep' },

      // Industrieweg (Y ≈ 3520) - Stoepen (Y ≈ 3450 & Y ≈ 3590)
      { x: 420, y: 3450, type: 'sidewalk', desc: 'Industriepark West Noord-Stoep' },
      { x: 1200, y: 3450, type: 'sidewalk', desc: 'Ambachtsweg Noord-Stoep' },
      { x: 1920, y: 3450, type: 'sidewalk', desc: 'Distributiecentrum Stoep' },
      { x: 2740, y: 3450, type: 'sidewalk', desc: 'Containerterminal Trottoir' },
      { x: 3520, y: 3450, type: 'sidewalk', desc: 'Techno-Campus Stoep' },

      { x: 420, y: 3590, type: 'sidewalk', desc: 'Industriepark West Zuid-Stoep' },
      { x: 1200, y: 3590, type: 'sidewalk', desc: 'Ambachtsweg Zuid-Stoep' },
      { x: 1920, y: 3590, type: 'sidewalk', desc: 'Fabrieksterrein Stoep' },
      { x: 2740, y: 3590, type: 'sidewalk', desc: 'Havendok Zuid-Stoep' },
      { x: 3520, y: 3590, type: 'sidewalk', desc: 'Techno-Campus Zuid' }
    ];

    // Veilige oversteekroutes over de weg (van stoep A naar stoep B via oversteekpunt / zebrapad)
    this.streetCrossings = [
      // Hoofdstraat (Y ≈ 1200) Oversteken
      { from: { x: 520, y: 1120 }, to: { x: 520, y: 1280 }, road: 'Hoofdstraat', zebraX: 520, roadY: 1200, axis: 'h' },
      { from: { x: 520, y: 1280 }, to: { x: 520, y: 1120 }, road: 'Hoofdstraat', zebraX: 520, roadY: 1200, axis: 'h' },
      { from: { x: 740, y: 1120 }, to: { x: 740, y: 1280 }, road: 'Hoofdstraat', zebraX: 740, roadY: 1200, axis: 'h' },
      { from: { x: 740, y: 1280 }, to: { x: 740, y: 1120 }, road: 'Hoofdstraat', zebraX: 740, roadY: 1200, axis: 'h' },
      { from: { x: 1060, y: 1120 }, to: { x: 1060, y: 1280 }, road: 'Hoofdstraat', zebraX: 1060, roadY: 1200, axis: 'h' },
      { from: { x: 1060, y: 1280 }, to: { x: 1060, y: 1120 }, road: 'Hoofdstraat', zebraX: 1060, roadY: 1200, axis: 'h' },
      { from: { x: 1460, y: 1120 }, to: { x: 1460, y: 1280 }, road: 'Hoofdstraat', zebraX: 1460, roadY: 1200, axis: 'h' },
      { from: { x: 1460, y: 1280 }, to: { x: 1460, y: 1120 }, road: 'Hoofdstraat', zebraX: 1460, roadY: 1200, axis: 'h' },
      { from: { x: 1780, y: 1120 }, to: { x: 1780, y: 1280 }, road: 'Hoofdstraat', zebraX: 1780, roadY: 1200, axis: 'h' },
      { from: { x: 1780, y: 1280 }, to: { x: 1780, y: 1120 }, road: 'Hoofdstraat', zebraX: 1780, roadY: 1200, axis: 'h' },
      { from: { x: 2680, y: 1120 }, to: { x: 2680, y: 1280 }, road: 'Hoofdstraat', zebraX: 2680, roadY: 1200, axis: 'h' },
      { from: { x: 2680, y: 1280 }, to: { x: 2680, y: 1120 }, road: 'Hoofdstraat', zebraX: 2680, roadY: 1200, axis: 'h' },

      // Noorderlaan (Y ≈ 420) Oversteken
      { from: { x: 680, y: 350 }, to: { x: 680, y: 490 }, road: 'Noorderlaan', roadY: 420, axis: 'h' },
      { from: { x: 680, y: 490 }, to: { x: 680, y: 350 }, road: 'Noorderlaan', roadY: 420, axis: 'h' },
      { from: { x: 1050, y: 350 }, to: { x: 1050, y: 490 }, road: 'Noorderlaan', roadY: 420, axis: 'h' },
      { from: { x: 1050, y: 490 }, to: { x: 1050, y: 350 }, road: 'Noorderlaan', roadY: 420, axis: 'h' },
      { from: { x: 1520, y: 350 }, to: { x: 1520, y: 490 }, road: 'Noorderlaan', roadY: 420, axis: 'h' },
      { from: { x: 1520, y: 490 }, to: { x: 1520, y: 350 }, road: 'Noorderlaan', roadY: 420, axis: 'h' },

      // Singelweg (Y ≈ 1920) Oversteken
      { from: { x: 760, y: 1850 }, to: { x: 760, y: 1990 }, road: 'Singelweg', roadY: 1920, axis: 'h' },
      { from: { x: 760, y: 1990 }, to: { x: 760, y: 1850 }, road: 'Singelweg', roadY: 1920, axis: 'h' },
      { from: { x: 1200, y: 1850 }, to: { x: 1200, y: 1990 }, road: 'Singelweg', roadY: 1920, axis: 'h' },
      { from: { x: 1200, y: 1990 }, to: { x: 1200, y: 1850 }, road: 'Singelweg', roadY: 1920, axis: 'h' },
      { from: { x: 1620, y: 1850 }, to: { x: 1620, y: 1990 }, road: 'Singelweg', roadY: 1920, axis: 'h' },
      { from: { x: 1620, y: 1990 }, to: { x: 1620, y: 1850 }, road: 'Singelweg', roadY: 1920, axis: 'h' },

      // Zuiderboulevard (Y ≈ 2740) Oversteken
      { from: { x: 1200, y: 2670 }, to: { x: 1200, y: 2810 }, road: 'Zuiderboulevard', roadY: 2740, axis: 'h' },
      { from: { x: 1200, y: 2810 }, to: { x: 1200, y: 2670 }, road: 'Zuiderboulevard', roadY: 2740, axis: 'h' },
      { from: { x: 2740, y: 2670 }, to: { x: 2740, y: 2810 }, road: 'Zuiderboulevard', roadY: 2740, axis: 'h' },
      { from: { x: 2740, y: 2810 }, to: { x: 2740, y: 2670 }, road: 'Zuiderboulevard', roadY: 2740, axis: 'h' },

      // Klinkerstraat & Bakkerijboulevard (Verticale assen) Oversteken
      { from: { x: 360, y: 1120 }, to: { x: 480, y: 1120 }, road: 'Klinkerstraat', roadX: 420, axis: 'v' },
      { from: { x: 480, y: 1120 }, to: { x: 360, y: 1120 }, road: 'Klinkerstraat', roadX: 420, axis: 'v' },
      { from: { x: 1130, y: 1850 }, to: { x: 1270, y: 1850 }, road: 'Bakkerijboulevard', roadX: 1200, axis: 'v' },
      { from: { x: 1270, y: 1850 }, to: { x: 1130, y: 1850 }, road: 'Bakkerijboulevard', roadX: 1200, axis: 'v' }
    ];

    // Backward compatibility pointer
    this.neighborhoodWaypoints = this.sidewalkWaypoints;

    // 4. Volwaardige dynamische 3D katten die actief door de stad zwerven op stoepen & veilig oversteken
    this.cats = [
      {
        id: 'kruimel',
        name: 'Kruimel',
        breed: 'ginger',
        x: 1040,
        y: 1080, // Voor de bakkerij
        targetX: 1260,
        targetY: 820,
        angle: -Math.PI / 2,
        speed: 1.05,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 0,
        tailPhase: 0,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'tijger',
        name: 'Tijger',
        breed: 'tabby',
        x: 920,
        y: 1080, // Pal naast ingang bakkerij
        targetX: 740,
        targetY: 1120,
        angle: 0,
        speed: 0.95,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 1.2,
        tailPhase: 1.5,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'minous',
        name: 'Minous',
        breed: 'calico',
        x: 1460,
        y: 1120, // Grand Café terras
        targetX: 1780,
        targetY: 1120,
        angle: 0,
        speed: 1.1,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 2.1,
        tailPhase: 0.8,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'boris',
        name: 'Boris',
        breed: 'black',
        x: 1520,
        y: 350, // Villawijk Noorderlaan
        targetX: 1880,
        targetY: 350,
        angle: 0,
        speed: 1.18,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 0.5,
        tailPhase: 2.3,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'luna',
        name: 'Luna',
        breed: 'siamese',
        x: 2160,
        y: 1120, // Prinsengracht kade
        targetX: 2160,
        targetY: 1280,
        angle: Math.PI / 2,
        speed: 1.15,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 3.4,
        tailPhase: 1.1,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'simba',
        name: 'Simba',
        breed: 'golden',
        x: 760,
        y: 1850, // Stadspark Singelweg
        targetX: 1200,
        targetY: 1850,
        angle: 0,
        speed: 1.0,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 1.8,
        tailPhase: 3.0,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'ollie',
        name: 'Ollie',
        breed: 'tuxedo',
        x: 1200,
        y: 2670, // Zuiderboulevard winkelcentrum
        targetX: 1920,
        targetY: 2670,
        angle: 0.2,
        speed: 1.05,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 2.7,
        tailPhase: 0.4,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'vlekkie',
        name: 'Vlekkie',
        breed: 'spotted',
        x: 520,
        y: 1280, // Klinkerhoek
        targetX: 740,
        targetY: 1280,
        angle: 0,
        speed: 0.98,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 0.9,
        tailPhase: 2.7,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'pip',
        name: 'Pip',
        breed: 'ginger',
        x: 3450,
        y: 1120, // Universiteitslaan
        targetX: 3450,
        targetY: 1850,
        angle: Math.PI / 2,
        speed: 1.1,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 1.1,
        tailPhase: 1.2,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'diesel',
        name: 'Diesel',
        breed: 'black',
        x: 2680,
        y: 1120, // Havenboulevard
        targetX: 2740,
        targetY: 2670,
        angle: Math.PI / 2,
        speed: 1.2,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 0.8,
        tailPhase: 2.1,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'binkie',
        name: 'Binkie',
        breed: 'golden',
        x: 360,
        y: 350, // Klinkerstraat Noord
        targetX: 680,
        targetY: 350,
        angle: 0,
        speed: 1.02,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 1.5,
        tailPhase: 0.9,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'snuf',
        name: 'Snuf',
        breed: 'calico',
        x: 1460,
        y: 1280, // Tandartspraktijk voortuin
        targetX: 1780,
        targetY: 1280,
        angle: 0,
        speed: 0.96,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 2.0,
        tailPhase: 1.8,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'pluisje',
        name: 'Pluisje',
        breed: 'siamese',
        x: 2160,
        y: 1990, // Notariskantoor Singelpark
        targetX: 1620,
        targetY: 1990,
        angle: Math.PI,
        speed: 1.08,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 0.7,
        tailPhase: 1.3,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'beer',
        name: 'Beer',
        breed: 'black',
        x: 1200,
        y: 3450, // Industriepark
        targetX: 1920,
        targetY: 3450,
        angle: 0,
        speed: 1.12,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 2.3,
        tailPhase: 0.6,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'sammy',
        name: 'Sammy',
        breed: 'tabby',
        x: 1200,
        y: 1990, // Parkvijver promenade
        targetX: 760,
        targetY: 1990,
        angle: Math.PI,
        speed: 0.97,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 1.9,
        tailPhase: 2.5,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      },
      {
        id: 'bella',
        name: 'Bella',
        breed: 'tuxedo',
        x: 1050,
        y: 350, // Noorderlaan Bakkerij Noord
        targetX: 1520,
        targetY: 350,
        angle: 0,
        speed: 1.04,
        state: 'walking',
        stateTimer: 0,
        walkCycle: 1.4,
        tailPhase: 1.6,
        speechText: '',
        speechTimer: 0,
        dead: false,
        deathTimer: 0,
        ghostY: 0,
        ghostAlpha: 1.0
      }
    ];

    // Backward compatibility pointer
    this.cat = this.cats[0];

    // 4. Stadsverkeer & AI-Verkeerssysteem: Dynamische AI-auto's op alle wegen van Knus & Krokant
    // Rijden rechts conform Nederlandse verkeersregels, stoppen bij stoplichten & interactie met speler
    this.carSpawnTimer = 0;
    this.targetCarCount = 20;
    this.carIdCounter = 1;
    this.cars = [
      {
        id: 'car_red',
        name: 'Rode Hatchback',
        road: 'Hoofdstraat',
        x: 250,
        y: 1250,
        laneY: 1250,
        angle: 0, // Oostwaarts (rechter rijstrook = zuidelijke baan Y=1250)
        speed: 1.8,
        targetSpeed: 1.8,
        length: 66,
        width: 34,
        color: '#dc2626',
        roofColor: '#991b1b',
        model: 'compact',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_yellow',
        name: 'Gele Kever',
        road: 'Hoofdstraat',
        x: 1350,
        y: 1250,
        laneY: 1250,
        angle: 0, // Oostwaarts
        speed: 1.7,
        targetSpeed: 1.7,
        length: 60,
        width: 34,
        color: '#eab308',
        roofColor: '#ca8a04',
        model: 'compact',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_blue',
        name: 'Blauwe Stationwagen',
        road: 'Hoofdstraat',
        x: 2100,
        y: 1150,
        laneY: 1150,
        angle: Math.PI, // Westwaarts (rechter rijstrook = noordelijke baan Y=1150)
        speed: 2.1,
        targetSpeed: 2.1,
        length: 76,
        width: 36,
        color: '#2563eb',
        roofColor: '#1d4ed8',
        model: 'station',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_white',
        name: 'Witte Sedan',
        road: 'Hoofdstraat',
        x: 900,
        y: 1150,
        laneY: 1150,
        angle: Math.PI, // Westwaarts
        speed: 1.9,
        targetSpeed: 1.9,
        length: 74,
        width: 35,
        color: '#f8fafc',
        roofColor: '#1e293b',
        model: 'sedan',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_taxi_1',
        name: 'Dorps-Taxi Jansen',
        road: 'Hoofdstraat',
        x: 3200,
        y: 1250,
        laneY: 1250,
        angle: 0,
        speed: 2.0,
        targetSpeed: 2.0,
        length: 74,
        width: 35,
        color: '#f59e0b',
        roofColor: '#d97706',
        model: 'taxi',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_orange',
        name: 'Oranje Stadsauto',
        road: 'Bakkerijboulevard',
        x: 1250,
        y: 2200,
        laneX: 1250,
        angle: -Math.PI / 2, // Noordwaarts (rechter rijstrook = oostelijke baan X=1250)
        speed: 1.9,
        targetSpeed: 1.9,
        length: 64,
        width: 34,
        color: '#ea580c',
        roofColor: '#c2410c',
        model: 'compact',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_green',
        name: 'Groene SUV',
        road: 'Bakkerijboulevard',
        x: 1150,
        y: 350,
        laneX: 1150,
        angle: Math.PI / 2, // Zuidwaarts (rechter rijstrook = westelijke baan X=1150)
        speed: 1.8,
        targetSpeed: 1.8,
        length: 74,
        width: 38,
        color: '#16a34a',
        roofColor: '#15803d',
        model: 'suv',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_cyan',
        name: 'Turquoise Cabrio',
        road: 'Klinkerstraat',
        x: 455,
        y: 1700,
        laneX: 455,
        angle: -Math.PI / 2, // Noordwaarts (oostelijke baan X=455)
        speed: 1.5,
        targetSpeed: 1.5,
        length: 58,
        width: 32,
        color: '#0891b2',
        roofColor: '#155e75',
        model: 'cabrio',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_navy_van',
        name: 'Donkerblauwe Bestelbus',
        road: 'Oostwal',
        x: 1885,
        y: 600,
        laneX: 1885,
        angle: Math.PI / 2, // Zuidwaarts (westelijke baan X=1885)
        speed: 1.7,
        targetSpeed: 1.7,
        length: 80,
        width: 40,
        color: '#1e3a8a',
        roofColor: '#172554',
        model: 'van',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_purple_compact',
        name: 'Paarse Compact',
        road: 'Noorderlaan',
        x: 1800,
        y: 385,
        laneY: 385,
        angle: Math.PI, // Westwaarts (noordelijke baan Y=385)
        speed: 1.7,
        targetSpeed: 1.7,
        length: 62,
        width: 34,
        color: '#9333ea',
        roofColor: '#7e22ce',
        model: 'compact',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_emerald_wagon',
        name: 'Smaragdgroene Stationwagen',
        road: 'Singelweg',
        x: 400,
        y: 1955,
        laneY: 1955,
        angle: 0, // Oostwaarts (zuidelijke baan Y=1955)
        speed: 1.8,
        targetSpeed: 1.8,
        length: 72,
        width: 36,
        color: '#059669',
        roofColor: '#047857',
        model: 'station',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_electric_blue',
        name: 'Elektrische Sedan',
        road: 'Zuiderboulevard',
        x: 800,
        y: 2790,
        laneY: 2790,
        angle: 0, // Oostwaarts
        speed: 2.1,
        targetSpeed: 2.1,
        length: 74,
        width: 36,
        color: '#0284c7',
        roofColor: '#0369a1',
        model: 'electric',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_haven_suv',
        name: 'Antraciet SUV',
        road: 'Havenboulevard',
        x: 2690,
        y: 1400,
        laneX: 2690,
        angle: Math.PI / 2, // Zuidwaarts
        speed: 1.85,
        targetSpeed: 1.85,
        length: 76,
        width: 38,
        color: '#334155',
        roofColor: '#1e293b',
        model: 'suv',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      {
        id: 'car_campus_compact',
        name: 'Robijnrode Compact',
        road: 'Universiteitslaan',
        x: 3570,
        y: 3100,
        laneX: 3570,
        angle: -Math.PI / 2, // Noordwaarts
        speed: 1.75,
        targetSpeed: 1.75,
        length: 62,
        width: 33,
        color: '#e11d48',
        roofColor: '#be123c',
        model: 'compact',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0
      },
      // 5. Nederlandse Politievoertuigen (Wijkpolitie & Noodhulp)
      {
        id: 'police_1',
        name: 'Politiepatrouille 01 (Wijkagent Bromsnor)',
        road: 'Hoofdstraat',
        isPolice: true,
        x: 600,
        y: 1150,
        laneY: 1150,
        angle: Math.PI, // Westwaarts (noordelijke baan Y=1150)
        speed: 2.1,
        targetSpeed: 2.1,
        length: 76,
        width: 36,
        color: '#ffffff',
        roofColor: '#ffffff',
        model: 'police',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0,
        sirenActive: false,
        sirenTimer: 0,
        state: 'patrol'
      },
      {
        id: 'police_2',
        name: 'Politiepatrouille 02 (Dienst Verkeer)',
        road: 'Bakkerijboulevard',
        isPolice: true,
        x: 1250,
        y: 1650,
        laneX: 1250,
        angle: -Math.PI / 2, // Noordwaarts (oostelijke baan X=1250)
        speed: 2.1,
        targetSpeed: 2.1,
        length: 76,
        width: 36,
        color: '#ffffff',
        roofColor: '#ffffff',
        model: 'police',
        isBraking: false,
        hornCooldown: 0,
        flashTimer: 0,
        hazardTimer: 0,
        hazardBlink: false,
        turnSignal: null,
        turnTimer: 0,
        speechText: '',
        speechTimer: 0,
        playerBlockTimer: 0,
        reactionDelayTimer: 0,
        sirenActive: false,
        sirenTimer: 0,
        state: 'patrol'
      }
    ];

    // Kruispunten voor voorrang en veilige kruisingsvrijgave (volledige 6x6 grid over 4800x4800)
    // Drukke kruispunten (langs de Avenues en Boulevards) zijn voorzien van verkeerslichten en flitspalen!
    this.intersections = [];
    const gridXs = [420, 1200, 1920, 2740, 3520, 4320];
    const gridYs = [420, 1200, 1920, 2740, 3520, 4320];
    const streetNamesX = {
      420: 'Klinkerstraat',
      1200: 'Bakkerijboulevard',
      1920: 'Oostwal',
      2740: 'Havenboulevard',
      3520: 'Universiteitslaan',
      4320: 'Oosterring'
    };
    const streetNamesY = {
      420: 'Noorderlaan',
      1200: 'Hoofdstraat',
      1920: 'Singelweg',
      2740: 'Zuiderboulevard',
      3520: 'Industrieweg',
      4320: 'Havenring Zuid'
    };

    let intCounter = 1;
    for (const iy of gridYs) {
      for (const ix of gridXs) {
        const isAvenueX = (ix === 1200 || ix === 2740);
        const isAvenueY = (iy === 1200 || iy === 2740);
        const isBusyIntersection = (isAvenueX || isAvenueY);
        // Spreiding van de cyclus-timer zodat niet alle lichten tegelijk verspringen
        const timerOffset = ((ix * 7 + iy * 11) % 160) / 10;

        // Sanering Flitspalen: slechts 3 strategische flitspalen in de hele stad (niet op elk kruispunt)
        const isSpeedCameraSpot = (
          (ix === 1200 && iy === 1200) || // Hoofdstraat × Bakkerijboulevard (Centraal)
          (ix === 2740 && iy === 2740) || // Zuiderboulevard × Havenboulevard (Zuid-Oost)
          (ix === 3520 && iy === 420)     // Noorderlaan × Universiteitslaan (Noord-Oost)
        );

        this.intersections.push({
          id: `int_${intCounter++}`,
          x: ix,
          y: iy,
          hw: isAvenueX ? 105 : 75,
          hh: isAvenueY ? 105 : 75,
          name: `${streetNamesY[iy] || 'Weg'} × ${streetNamesX[ix] || 'Straat'}`,
          isBusy: isBusyIntersection,
          hasTrafficLight: isBusyIntersection,
          trafficLight: isBusyIntersection ? {
            timer: timerOffset,
            cycleDuration: 16.0,
            horizontal: 'green',
            vertical: 'red',
            hasSpeedCamera: isSpeedCameraSpot
          } : null,
          redViolatedTimer: 0
        });
      }
    }

    // 5. Gouden Muntjes verspreid door de 4x grotere stad om op te pikken (60+ locaties)
    this.collectibleCoins = [
      // Centrum & Bakkerij Kwartier
      { id: 'coin_1', x: 1220, y: 1040, value: 5, collected: false, respawnTimer: 0, animTimer: 0.1 },
      { id: 'coin_2', x: 1420, y: 890, value: 1, collected: false, respawnTimer: 0, animTimer: 0.7 },
      { id: 'coin_3', x: 1620, y: 900, value: 1, collected: false, respawnTimer: 0, animTimer: 1.3 },
      { id: 'coin_4', x: 500, y: 330, value: 1, collected: false, respawnTimer: 0, animTimer: 1.9 },
      { id: 'coin_5', x: 800, y: 330, value: 5, collected: false, respawnTimer: 0, animTimer: 2.5 },
      { id: 'coin_6', x: 1450, y: 330, value: 1, collected: false, respawnTimer: 0, animTimer: 3.1 },
      { id: 'coin_7', x: 1830, y: 650, value: 1, collected: false, respawnTimer: 0, animTimer: 3.7 },
      { id: 'coin_8', x: 1830, y: 1650, value: 1, collected: false, respawnTimer: 0, animTimer: 4.3 },
      { id: 'coin_9', x: 330, y: 950, value: 1, collected: false, respawnTimer: 0, animTimer: 4.9 },
      { id: 'coin_10', x: 330, y: 450, value: 5, collected: false, respawnTimer: 0, animTimer: 5.5 },
      { id: 'coin_11', x: 600, y: 1100, value: 1, collected: false, respawnTimer: 0, animTimer: 0.3 },
      { id: 'coin_12', x: 1020, y: 1080, value: 1, collected: false, respawnTimer: 0, animTimer: 1.1 },
      { id: 'coin_13', x: 1380, y: 1080, value: 1, collected: false, respawnTimer: 0, animTimer: 1.7 },
      { id: 'coin_14', x: 450, y: 1830, value: 1, collected: false, respawnTimer: 0, animTimer: 2.3 },
      { id: 'coin_15', x: 1100, y: 1830, value: 5, collected: false, respawnTimer: 0, animTimer: 2.9 },
      { id: 'coin_16', x: 1750, y: 1830, value: 1, collected: false, respawnTimer: 0, animTimer: 3.5 },
      { id: 'coin_17', x: 220, y: 1350, value: 1, collected: false, respawnTimer: 0, animTimer: 4.1 },
      { id: 'coin_18', x: 2120, y: 1260, value: 1, collected: false, respawnTimer: 0, animTimer: 4.7 },
      { id: 'coin_19', x: 1180, y: 220, value: 1, collected: false, respawnTimer: 0, animTimer: 5.3 },
      { id: 'coin_20', x: 1220, y: 2150, value: 5, collected: false, respawnTimer: 0, animTimer: 5.9 },

      // Havenkwartier & Grachten (Noord-Oost)
      { id: 'coin_21', x: 2300, y: 840, value: 1, collected: false, respawnTimer: 0, animTimer: 0.2 },
      { id: 'coin_22', x: 2550, y: 360, value: 1, collected: false, respawnTimer: 0, animTimer: 0.8 },
      { id: 'coin_23', x: 3000, y: 340, value: 5, collected: false, respawnTimer: 0, animTimer: 1.4 },
      { id: 'coin_24', x: 3400, y: 340, value: 1, collected: false, respawnTimer: 0, animTimer: 2.0 },
      { id: 'coin_25', x: 3820, y: 280, value: 10, collected: false, respawnTimer: 0, animTimer: 2.6 }, // Vuurtoren jackpot
      { id: 'coin_26', x: 4100, y: 340, value: 1, collected: false, respawnTimer: 0, animTimer: 3.2 },
      { id: 'coin_27', x: 4500, y: 300, value: 5, collected: false, respawnTimer: 0, animTimer: 3.8 },
      { id: 'coin_28', x: 2950, y: 840, value: 1, collected: false, respawnTimer: 0, animTimer: 4.4 },
      { id: 'coin_29', x: 3300, y: 840, value: 1, collected: false, respawnTimer: 0, animTimer: 5.0 },
      { id: 'coin_30', x: 3750, y: 840, value: 5, collected: false, respawnTimer: 0, animTimer: 5.6 },
      { id: 'coin_31', x: 4480, y: 840, value: 1, collected: false, respawnTimer: 0, animTimer: 0.4 },

      // Cultuurwijk & Oosterpark (Midden-Oost)
      { id: 'coin_32', x: 2850, y: 1300, value: 1, collected: false, respawnTimer: 0, animTimer: 1.0 },
      { id: 'coin_33', x: 3100, y: 1300, value: 5, collected: false, respawnTimer: 0, animTimer: 1.6 },
      { id: 'coin_34', x: 3600, y: 1300, value: 1, collected: false, respawnTimer: 0, animTimer: 2.2 },
      { id: 'coin_35', x: 3850, y: 1250, value: 5, collected: false, respawnTimer: 0, animTimer: 2.8 },
      { id: 'coin_36', x: 4400, y: 1300, value: 1, collected: false, respawnTimer: 0, animTimer: 3.4 },
      { id: 'coin_37', x: 2300, y: 2050, value: 5, collected: false, respawnTimer: 0, animTimer: 4.0 },
      { id: 'coin_38', x: 2950, y: 2050, value: 1, collected: false, respawnTimer: 0, animTimer: 4.6 },
      { id: 'coin_39', x: 3750, y: 2050, value: 5, collected: false, respawnTimer: 0, animTimer: 5.2 },
      { id: 'coin_40', x: 4480, y: 2050, value: 1, collected: false, respawnTimer: 0, animTimer: 5.8 },

      // Industrie & Ambacht (Zuid-West)
      { id: 'coin_41', x: 240, y: 2050, value: 1, collected: false, respawnTimer: 0, animTimer: 0.5 },
      { id: 'coin_42', x: 620, y: 2050, value: 5, collected: false, respawnTimer: 0, animTimer: 1.1 },
      { id: 'coin_43', x: 920, y: 2050, value: 1, collected: false, respawnTimer: 0, animTimer: 1.7 },
      { id: 'coin_44', x: 1450, y: 2050, value: 1, collected: false, respawnTimer: 0, animTimer: 2.3 },
      { id: 'coin_45', x: 220, y: 2850, value: 5, collected: false, respawnTimer: 0, animTimer: 2.9 },
      { id: 'coin_46', x: 650, y: 2850, value: 1, collected: false, respawnTimer: 0, animTimer: 3.5 },
      { id: 'coin_47', x: 920, y: 2850, value: 1, collected: false, respawnTimer: 0, animTimer: 4.1 },
      { id: 'coin_48', x: 1450, y: 2850, value: 5, collected: false, respawnTimer: 0, animTimer: 4.7 },
      { id: 'coin_49', x: 2200, y: 2850, value: 1, collected: false, respawnTimer: 0, animTimer: 5.3 },
      { id: 'coin_50', x: 240, y: 3650, value: 1, collected: false, respawnTimer: 0, animTimer: 5.9 },
      { id: 'coin_51', x: 620, y: 3650, value: 5, collected: false, respawnTimer: 0, animTimer: 0.3 },
      { id: 'coin_52', x: 1450, y: 3650, value: 1, collected: false, respawnTimer: 0, animTimer: 0.9 },
      { id: 'coin_53', x: 650, y: 4370, value: 5, collected: false, respawnTimer: 0, animTimer: 1.5 },
      { id: 'coin_54', x: 1450, y: 4370, value: 10, collected: false, respawnTimer: 0, animTimer: 2.1 }, // Kinderboerderij appelbollen jackpot

      // Campus Zuid & Zuiderkwartier (Zuid-Oost)
      { id: 'coin_55', x: 3000, y: 2850, value: 1, collected: false, respawnTimer: 0, animTimer: 2.7 },
      { id: 'coin_56', x: 3750, y: 2850, value: 5, collected: false, respawnTimer: 0, animTimer: 3.3 },
      { id: 'coin_57', x: 4450, y: 2850, value: 1, collected: false, respawnTimer: 0, animTimer: 3.9 },
      { id: 'coin_58', x: 3000, y: 3650, value: 5, collected: false, respawnTimer: 0, animTimer: 4.5 },
      { id: 'coin_59', x: 3750, y: 3650, value: 1, collected: false, respawnTimer: 0, animTimer: 5.1 },
      { id: 'coin_60', x: 4450, y: 3650, value: 5, collected: false, respawnTimer: 0, animTimer: 5.7 },
      { id: 'coin_61', x: 3000, y: 4370, value: 5, collected: false, respawnTimer: 0, animTimer: 0.6 },
      { id: 'coin_62', x: 3820, y: 4370, value: 10, collected: false, respawnTimer: 0, animTimer: 1.2 }, // Observatorium sterren jackpot
      { id: 'coin_63', x: 4450, y: 4370, value: 1, collected: false, respawnTimer: 0, animTimer: 1.8 }
    ];
  }

  /** Haalt alle politiewagens weg; de stad zet er binnen een paar seconden twee nieuwe op patrouille aan de rand. */
  removePolice() {
    if (!this.cars) return;
    for (let i = this.cars.length - 1; i >= 0; i--) if (this.cars[i].isPolice) this.cars.splice(i, 1);
  }

  /** Zet de afgesloten straten (marktdag) en haal auto's weg die al in de afsluiting staan. */
  setClosures(list) {
    this.closures = list.map((c) => ({ ...c }));
    for (const cl of this.closures) {
      for (let i = (this.cars?.length ?? 0) - 1; i >= 0; i--) {
        const car = this.cars[i];
        if (car.isPolice) continue;
        if (car.x > cl.x - 30 && car.x < cl.x + cl.w + 30 && car.y > cl.y - 20 && car.y < cl.y + cl.h + 20) {
          this.cars.splice(i, 1);
          this.spawnAiCar();
        }
      }
    }
  }

  getBuildings() {
    const list = [
      { x: this.bakery.x, y: this.bakery.y, w: this.bakery.w, h: this.bakery.h, label: this.bakery.name },
      ...this.buildings
    ];
    // Afgesloten straten (marktdag) zijn voor de scooter net zo massief als een gebouw
    for (const cl of this.closures || []) list.push({ x: cl.x, y: cl.y, w: cl.w, h: cl.h, label: cl.name, isClosure: true });
    if (Array.isArray(this.roadWorks)) {
      for (const rw of this.roadWorks) {
        list.push({ x: rw.x, y: rw.y, w: rw.w, h: rw.h, label: rw.label, isRoadWork: true });
      }
    } else if (this.roadWorks) {
      list.push({ x: this.roadWorks.x, y: this.roadWorks.y, w: this.roadWorks.w, h: this.roadWorks.h, label: this.roadWorks.label, isRoadWork: true });
    }
    return list;
  }

  // Detect if vehicle is driving in a sand/gravel excavation trench
  checkSandTrench(px, py) {
    if (!this.roadWorks) return null;
    const list = Array.isArray(this.roadWorks) ? this.roadWorks : [this.roadWorks];
    for (const rw of list) {
      if (rw.sand) {
        const s = rw.sand;
        if (px >= s.x && px <= s.x + s.w && py >= s.y && py <= s.y + s.h) {
          return rw;
        }
      }
    }
    return null;
  }

  // Multi-point physical collision preventing ANY part of the scooter from entering buildings
  resolveScooterCollision(scooter) {
    const list = this.getBuildings();
    let hadCollision = false;
    let maxImpact = 0;
    // Som van alle duwrichtingen: de botsingsnormaal, zodat de scooter langs muren kan schampen.
    let normalX = 0;
    let normalY = 0;

    // 3 physical sample discs along the vehicle chassis:
    // Scooter: 3 compact discs | Delivery Van: 3 wider discs for van chassis
    const isVan = (scooter.vehicleType === 'van');
    const cosA = Math.cos(scooter.angle);
    const sinA = Math.sin(scooter.angle);

    const samplePoints = isVan ? [
      { offset: 30, radius: 17 },  // Van front bumper & hood
      { offset: 0, radius: 17 },   // Van cabin & middle
      { offset: -30, radius: 17 }  // Van rear thermo cargo area
    ] : [
      { offset: 20, radius: 12 },  // Front wheel & mudguard
      { offset: 0, radius: 14 },   // Center chassis & handlebars
      { offset: -20, radius: 13 }  // Rear delivery box
    ];

    // Iterative relaxation passes to resolve multi-corner or acute angle collisions
    for (let iter = 0; iter < 4; iter++) {
      let iterCollision = false;

      for (let pIdx = 0; pIdx < samplePoints.length; pIdx++) {
        const sp = samplePoints[pIdx];
        const px = scooter.x + cosA * sp.offset;
        const py = scooter.y + sinA * sp.offset;

        // 1. Gebouwen botsingdetectie
        for (let bIdx = 0; bIdx < list.length; bIdx++) {
          const b = list[bIdx];
          const cx = Math.max(b.x, Math.min(b.x + b.w, px));
          const cy = Math.max(b.y, Math.min(b.y + b.h, py));

          const dx = px - cx;
          const dy = py - cy;
          const distSq = dx * dx + dy * dy;

          if (distSq < sp.radius * sp.radius) {
            const dist = Math.sqrt(distSq);
            let nx = 0;
            let ny = 0;
            let pen = sp.radius - dist;

            if (dist > 0.0001) {
              nx = dx / dist;
              ny = dy / dist;
            } else {
              // Deep interior penetration: push out along nearest exterior building edge
              const leftPen = px - b.x;
              const rightPen = (b.x + b.w) - px;
              const topPen = py - b.y;
              const bottomPen = (b.y + b.h) - py;
              const minPen = Math.min(leftPen, rightPen, topPen, bottomPen);

              if (minPen === leftPen) { nx = -1; pen = leftPen + sp.radius; }
              else if (minPen === rightPen) { nx = 1; pen = rightPen + sp.radius; }
              else if (minPen === topPen) { ny = -1; pen = topPen + sp.radius; }
              else { ny = 1; pen = bottomPen + sp.radius; }
            }

            // Push vehicle center cleanly away from the building
            scooter.x += nx * pen;
            scooter.y += ny * pen;
            normalX += nx * pen;
            normalY += ny * pen;
            iterCollision = true;
            hadCollision = true;
            maxImpact = Math.max(maxImpact, Math.abs(scooter.speed));
          }
        }

        // 2. Grachten & Kademuren botsingdetectie (Je kunt niet in de sloot/gracht rijden!)
        if (this.canals) {
          // Als de speler bezig is met een schanssprong (airborne), vlieg je vrij over het water!
          if (scooter.isAirborne && (scooter.jumpZ || 0) > 0.3) {
            // Vrij door de lucht vliegen boven de gracht
          } else {
            const bridge = this.drawBridges ? this.drawBridges[0] : null;
            for (const canal of this.canals) {
              const waterLeft = canal.x - 4;
              const waterRight = canal.x + canal.w + 4;
              const inCanalY = (py >= canal.y && py <= canal.y + canal.h);

              if (inCanalY && px > waterLeft - sp.radius && px < waterRight + sp.radius) {
                const onBridgeY = bridge && (py >= bridge.deckY + 10 && py <= bridge.deckY + bridge.deckH - 10);
                
                if (onBridgeY) {
                  const prog = bridge.openProgress || 0;
                  if (prog <= 0.08) {
                    // Brug is potdicht: vrije doorgang!
                  } else {
                    // Brug is open (twee opstaande schansen met open water ertussen)
                    const halfSpan = bridge.deckW / 2; // 60px
                    const leafWidth = Math.max(10, halfSpan * (1 - prog * 0.78));
                    const westTipX = bridge.deckX + leafWidth;
                    const eastTipX = bridge.deckX + bridge.deckW - leafWidth;

                    // Als de speler zich in de open waterkolk tussen de twee schansen bevindt
                    if (px > westTipX - sp.radius && px < eastTipX + sp.radius) {
                      const canalCenterX = bridge.deckX + bridge.deckW / 2;
                      let pushX = 0;
                      if (px < canalCenterX) {
                        // Duw terug op de Westelijke schans
                        const targetX = westTipX - sp.radius - 2;
                        pushX = targetX - px;
                      } else {
                        // Duw terug op de Oostelijke schans
                        const targetX = eastTipX + sp.radius + 2;
                        pushX = targetX - px;
                      }
                      scooter.x += pushX;
                      normalX += pushX;
                      scooter.speed *= 0.4;
                      iterCollision = true;
                      hadCollision = true;
                      maxImpact = Math.max(maxImpact, Math.abs(scooter.speed));
                    }
                  }
                } else {
                  // Buiten de brug: kade / sloot botsing
                  const canalCenterX = canal.x + canal.w / 2;
                  let pushX = 0;
                  if (px < canalCenterX) {
                    const targetX = waterLeft - sp.radius - 2;
                    pushX = targetX - px;
                  } else {
                    const targetX = waterRight + sp.radius + 2;
                    pushX = targetX - px;
                  }
                  scooter.x += pushX;
                  normalX += pushX;
                  scooter.speed *= 0.4;
                  iterCollision = true;
                  hadCollision = true;
                  maxImpact = Math.max(maxImpact, Math.abs(scooter.speed));
                }
              }
            }
          }
        }
      }

      if (!iterCollision) break;
    }

    // World boundary clamping
    const minBound = 60;
    const maxBoundX = this.width - 60;
    const maxBoundY = this.height - 60;
    if (scooter.x < minBound) { scooter.x = minBound; hadCollision = true; }
    if (scooter.x > maxBoundX) { scooter.x = maxBoundX; hadCollision = true; }
    if (scooter.y < minBound) { scooter.y = minBound; hadCollision = true; }
    if (scooter.y > maxBoundY) { scooter.y = maxBoundY; hadCollision = true; }

    return { hadCollision, maxImpact, normalX, normalY };
  }

  // Multi-point physical barrier preventing Knus Expres van from clipping or entering buildings
  resolveVanBuildingCollision(van) {
    if (!van) return;
    const list = this.getBuildings();
    const cosA = Math.cos(van.angle);
    const sinA = Math.sin(van.angle);
    // Perpendicular normal vectors
    const normX = -sinA;
    const normY = cosA;

    // 5 multi-disc probes covering the 96x46 van chassis
    const samplePoints = [
      { x: cosA * 36, y: sinA * 36, r: 22 },   // Front bumper
      { x: 0, y: 0, r: 23 },                   // Center chassis
      { x: -cosA * 36, y: -sinA * 36, r: 22 }, // Rear bumper
      { x: normX * 18, y: normY * 18, r: 18 }, // Left flank
      { x: -normX * 18, y: -normY * 18, r: 18 }// Right flank
    ];

    for (let iter = 0; iter < 3; iter++) {
      let collided = false;
      for (let pIdx = 0; pIdx < samplePoints.length; pIdx++) {
        const sp = samplePoints[pIdx];
        const px = van.x + sp.x;
        const py = van.y + sp.y;

        for (let bIdx = 0; bIdx < list.length; bIdx++) {
          const b = list[bIdx];
          const cx = Math.max(b.x, Math.min(b.x + b.w, px));
          const cy = Math.max(b.y, Math.min(b.y + b.h, py));

          const dx = px - cx;
          const dy = py - cy;
          const distSq = dx * dx + dy * dy;

          if (distSq < sp.r * sp.r) {
            collided = true;
            const dist = Math.sqrt(distSq);
            let nx = 0;
            let ny = 0;
            let pen = sp.r - dist;

            if (dist > 0.0001) {
              nx = dx / dist;
              ny = dy / dist;
            } else {
              const leftPen = px - b.x;
              const rightPen = (b.x + b.w) - px;
              const topPen = py - b.y;
              const bottomPen = (b.y + b.h) - py;
              const minPen = Math.min(leftPen, rightPen, topPen, bottomPen);

              if (minPen === leftPen) { nx = -1; pen = leftPen + sp.r; }
              else if (minPen === rightPen) { nx = 1; pen = rightPen + sp.r; }
              else if (minPen === topPen) { ny = -1; pen = topPen + sp.r; }
              else { ny = 1; pen = bottomPen + sp.r; }
            }

            // Push van bodily outside the building obstacle
            van.x += nx * pen;
            van.y += ny * pen;
          }
        }
      }
      if (!collided) break;
    }

    // World boundary clamping for van
    const minBound = 50;
    const maxBoundX = this.width - 50;
    const maxBoundY = this.height - 50;
    if (van.x < minBound) van.x = minBound;
    if (van.x > maxBoundX) van.x = maxBoundX;
    if (van.y < minBound) van.y = minBound;
    if (van.y > maxBoundY) van.y = maxBoundY;
  }

  checkBuildingCollision(px, py, radius = 16) {
    const list = this.getBuildings();
    for (let i = 0; i < list.length; i++) {
      const b = list[i];
      const cx = Math.max(b.x, Math.min(b.x + b.w, px));
      const cy = Math.max(b.y, Math.min(b.y + b.h, py));

      const dx = px - cx;
      const dy = py - cy;
      const distSq = dx * dx + dy * dy;

      if (distSq < radius * radius) {
        return { collided: true, building: b };
      }
    }
    return null;
  }

  // Detecteert of een punt in of vlakbij een stilstaande/geparkeerde auto ligt (zodat katten er niet onderdoor lopen)
  checkStationaryCarCollision(px, py, radius = 14) {
    const checkList = [];
    if (this.cars) checkList.push(...this.cars);
    if (this.trafficJams) {
      for (const jam of this.trafficJams) {
        if (jam.active && jam.cars) checkList.push(...jam.cars);
      }
    }
    if (this.parkedVan) checkList.push(this.parkedVan);

    for (const car of checkList) {
      if (!car) continue;
      // Alleen auto's die stilstaan, geparkeerd staan of in de remmen knijpen
      const isStationary = (typeof car.speed !== 'number' || Math.abs(car.speed) < 0.35 || car.isBraking || car.speed === 0);
      if (!isStationary) continue;

      const cLength = (car.length || 70) + radius * 2;
      const cWidth = (car.width || 36) + radius * 2;
      
      const cAngle = car.angle || 0;
      const cosA = Math.cos(-cAngle);
      const sinA = Math.sin(-cAngle);
      
      const relX = px - car.x;
      const relY = py - car.y;
      const localX = relX * cosA - relY * sinA;
      const localY = relX * sinA + relY * cosA;

      if (Math.abs(localX) < cLength / 2 && Math.abs(localY) < cWidth / 2) {
        return {
          collided: true,
          car,
          deflectX: (localX >= 0 ? 1 : -1) * (cLength / 2 + 6),
          deflectY: (localY >= 0 ? 1 : -1) * (cWidth / 2 + 6),
          angle: cAngle
        };
      }
    }
    return null;
  }

  isPointOnRoad(px, py) {
    for (const r of this.roads) {
      if (px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h) {
        return { onRoad: true, isKlinkers: !!r.klinkers };
      }
    }
    return { onRoad: false, isKlinkers: false };
  }

  checkSpeedBump(px, py) {
    for (const b of this.speedBumps) {
      if (px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h) {
        return b;
      }
    }
    return null;
  }

  checkPuddle(px, py) {
    for (const p of this.puddles) {
      const dx = px - p.x;
      const dy = py - p.y;
      if (dx * dx + dy * dy < p.r * p.r) {
        return p;
      }
    }
    return null;
  }

  // Dynamic Obstacles Update loop (Fase 2 & Stadsconcurrent Knus Expres)
  update(dt, soundEngine, scooter = null) {
    this.playerRef = scooter;
    this.crossingCats = this.cats ? this.cats.filter((c) => !c.dead && (c.state === 'crossing' || c.state === 'waiting_curb')).length : 0;
    // 0. Update Verkeerslichten op drukke kruispunten (16-seconden cyclus)
    if (this.intersections) {
      for (const inter of this.intersections) {
        if (!inter.hasTrafficLight || !inter.trafficLight) continue;
        const tl = inter.trafficLight;
        tl.timer = (tl.timer + dt) % tl.cycleDuration;

        // Nederlandse verkeerslichtencyclus:
        // 0.0 - 6.5s: Horizontaal GROEN, Verticaal ROOD
        // 6.5 - 8.5s: Horizontaal ORANJE, Verticaal ROOD
        // 8.5 - 9.5s: ALLES ROOD (ontruimingstijd)
        // 9.5 - 14.0s: Verticaal GROEN, Horizontaal ROOD
        // 14.0 - 15.5s: Verticaal ORANJE, Horizontaal ROOD
        // 15.5 - 16.0s: ALLES ROOD (ontruimingstijd)
        if (tl.timer < 6.5) {
          tl.horizontal = 'green';
          tl.vertical = 'red';
        } else if (tl.timer < 8.5) {
          tl.horizontal = 'orange';
          tl.vertical = 'red';
        } else if (tl.timer < 9.5) {
          tl.horizontal = 'red';
          tl.vertical = 'red';
        } else if (tl.timer < 14.0) {
          tl.horizontal = 'red';
          tl.vertical = 'green';
        } else if (tl.timer < 15.5) {
          tl.horizontal = 'red';
          tl.vertical = 'orange';
        } else {
          tl.horizontal = 'red';
          tl.vertical = 'red';
        }

        if (inter.redViolatedTimer > 0) {
          inter.redViolatedTimer -= dt;
        }
      }
    }

    // 1. Actieve Stadsconcurrent Knus Expres (beweging, bezorgingen & interactie)
    if (this.van) {
      this.updateVan(dt, soundEngine, scooter);
    }

    // 2. Traffic cones physics (velocity & friction decay)
    for (const c of this.cones) {
      if (c.knocked) {
        c.x += c.vx;
        c.y += c.vy;
        c.vx *= 0.88;
        c.vy *= 0.88;
        if (Math.abs(c.vx) < 0.04 && Math.abs(c.vy) < 0.04) {
          c.vx = 0;
          c.vy = 0;
        }
      }
    }

    // 2b. Dynamische Tijdelijke Files & Opstoppingen (Traffic Jams met Echte Auto's)
    if (this.trafficJams) {
      for (const jam of this.trafficJams) {
        jam.timer -= dt;
        if (jam.timer <= 0) {
          jam.active = !jam.active;
          jam.timer = jam.active ? (30 + Math.random() * 25) : (35 + Math.random() * 30);
          if (jam.active && soundEngine && Math.random() < 0.65) {
            soundEngine.playTrafficCarHorn();
          }
        }
        if (jam.slalomCooldown > 0) {
          jam.slalomCooldown -= dt;
        }

        // Stationaire auto-animaties (uitlaatwolkjes & motortrilling)
        if (jam.active && jam.cars) {
          for (const car of jam.cars) {
            car.exhaustTimer = (car.exhaustTimer || 0) + dt;
            car.vibration = Math.sin(Date.now() * 0.025 + (car.x || 0) * 0.1) * 0.4;
            if (!car.smokeParticles) car.smokeParticles = [];

            if (car.exhaustTimer > 0.7) {
              car.exhaustTimer = Math.random() * 0.2;
              const tailX = car.x - Math.cos(car.angle) * (car.length / 2 + 2) + Math.sin(car.angle) * 7;
              const tailY = car.y - Math.sin(car.angle) * (car.length / 2 + 2) - Math.cos(car.angle) * 7;
              car.smokeParticles.push({
                x: tailX,
                y: tailY,
                vx: -Math.cos(car.angle) * 0.25 + (Math.random() - 0.5) * 0.2,
                vy: -Math.sin(car.angle) * 0.25 - 0.15,
                size: 2.2,
                alpha: 0.5
              });
            }

            for (let pIdx = car.smokeParticles.length - 1; pIdx >= 0; pIdx--) {
              const p = car.smokeParticles[pIdx];
              p.x += p.vx;
              p.y += p.vy;
              p.size += dt * 3.2;
              p.alpha -= dt * 0.5;
              if (p.alpha <= 0) car.smokeParticles.splice(pIdx, 1);
            }
          }

          // Incidenteel ongeduldig toeteren in de file als speler in de buurt is
          if (scooter && soundEngine && Math.random() < 0.002) {
            const distSq = (scooter.x - (jam.x + jam.w / 2)) ** 2 + (scooter.y - (jam.y + jam.h / 2)) ** 2;
            if (distSq < 320 * 320) {
              soundEngine.playTrafficCarHorn();
            }
          }
        }
      }
    }

    // 3. Dynamische 3D Katten update: lopen over de stoep, steken soms over & stoppen voor naderende auto's
    for (const cat of this.cats) {
      if (cat.dead) {
        if (cat.deathTimer < 1.0) {
          cat.deathTimer += dt * 2.0;
        }
        if (cat.ghostAlpha > 0) {
          cat.ghostY -= dt * 26;
          cat.ghostAlpha = Math.max(0, cat.ghostAlpha - dt * 0.16);
        }
        continue;
      }

      // Spraak- & gedachteballon timer updaten
      if (cat.speechTimer > 0) {
        cat.speechTimer -= dt;
        if (cat.speechTimer <= 0) cat.speechText = '';
      }

      // Schrikreactie bij rakelings passerende of snelle scooter van de speler
      if (scooter) {
        const dX = scooter.x - cat.x;
        const dY = scooter.y - cat.y;
        const distSq = dX * dX + dY * dY;
        if (distSq < 70 * 70 && Math.abs(scooter.speed) > 1.8 && cat.state !== 'fleeing') {
          cat.state = 'fleeing';
          cat.stateTimer = 1.6;
          cat.angle = Math.atan2(-dY, -dX);
          cat.speechText = t('chat.cat.scared');
          cat.speechTimer = 1.8;
          if (soundEngine && Math.random() < 0.45) soundEngine.playCatMeow();
        }
      }

      const mapMaxX = this.width - 60;
      const mapMaxY = this.height - 60;
      if (cat.crossCooldown > 0) cat.crossCooldown -= dt;

      // Persoonlijke ruimte: een kat stapt opzij voor de scooter en loopt er nooit onderdoor, ook niet als je stilstaat.
      if (scooter && cat.state !== 'fleeing') {
        const pdx = cat.x - scooter.x;
        const pdy = cat.y - scooter.y;
        const pd = Math.hypot(pdx, pdy);
        if (pd < CAT_PERSONAL_SPACE) {
          const away = pd > 0.5 ? Math.atan2(pdy, pdx) : cat.angle;
          const stepAway = 1.7 * 60 * dt;
          const ax = cat.x + Math.cos(away) * stepAway;
          const ay = cat.y + Math.sin(away) * stepAway;
          if (!this.checkBuildingCollision(ax, ay, 10)) {
            cat.x = Math.max(60, Math.min(mapMaxX, ax));
            cat.y = Math.max(60, Math.min(mapMaxY, ay));
          }
          // Een kat die wegloopt van de scooter kiest een bestemming aan de andere kant
          if (cat.state === 'walking' && Math.hypot(cat.targetX - scooter.x, cat.targetY - scooter.y) < CAT_PERSONAL_SPACE + 40) {
            this.pickNewCatTarget(cat);
          }
          cat.walkCycle = (cat.walkCycle || 0) + stepAway * 0.18;
        }
      }

      if (cat.state === 'fleeing') {
        cat.stateTimer -= dt;
        const runSpeed = 3.2;
        const nextX = cat.x + Math.cos(cat.angle) * runSpeed;
        const nextY = cat.y + Math.sin(cat.angle) * runSpeed;
        
        // Zorg dat vluchtende kat niet onder stilstaande auto's of gebouwen door rent
        if (!this.checkBuildingCollision(nextX, nextY, 10) && !this.checkStationaryCarCollision(nextX, nextY, 12)) {
          cat.x = Math.max(60, Math.min(mapMaxX, nextX));
          cat.y = Math.max(60, Math.min(mapMaxY, nextY));
        } else {
          cat.angle += Math.PI * 0.5;
        }

        cat.walkCycle = (cat.walkCycle || 0) + dt * 22;
        cat.tailPhase = (cat.tailPhase || 0) + dt * 12;

        if (cat.stateTimer <= 0) {
          cat.state = 'walking';
          this.pickNewCatTarget(cat);
        }
      } else if (cat.state === 'waiting_curb') {
        // =========================================================================
        // KAT AAN DE STOEPRAND: Kijkt links en rechts & stopt zodra er een auto nadert
        // =========================================================================
        cat.stateTimer -= dt;
        cat.tailPhase = (cat.tailPhase || 0) + dt * 3.5;

        // Detecteer naderende AI-stadsauto's, de bezorgbus of snel rijdende speler
        let carApproaching = false;
        let closestCarDist = 999;
        let oncomingCar = null;

        if (this.cars) {
          for (const car of this.cars) {
            const dxC = car.x - cat.x;
            const dyC = car.y - cat.y;
            const distCar = Math.hypot(dxC, dyC);
            
            // Controleer of de auto naar de oversteekplek toe rijdt
            const fx = Math.cos(car.angle);
            const fy = Math.sin(car.angle);
            const dotForward = -(dxC * fx + dyC * fy); // Positief als de auto richting de kat rijdt

            if (distCar < 210 && dotForward > -20 && Math.abs(car.speed) > 0.35) {
              carApproaching = true;
              if (distCar < closestCarDist) {
                closestCarDist = distCar;
                oncomingCar = car;
              }
            }
          }
        }

        // Ook controleren op de bestelbus van Stadsconcurrent Knus Expres
        if (this.van) {
          const dxV = this.van.x - cat.x;
          const dyV = this.van.y - cat.y;
          const distV = Math.hypot(dxV, dyV);
          if (distV < 220 && Math.abs(this.van.speed || 0) > 0.4) {
            carApproaching = true;
          }
        }

        // Speler scooter die hard aan komt rijden, of die dichtbij het oversteekpunt staat
        if (scooter) {
          const dxS = scooter.x - cat.x;
          const dyS = scooter.y - cat.y;
          const distS = Math.hypot(dxS, dyS);
          if ((Math.abs(scooter.speed) > 1.5 && distS < 170) || distS < 150) {
            carApproaching = true;
          }
        }

        if (carApproaching) {
          // Er nadert verkeer: kat blijft netjes en geduldig op de stoeprand wachten!
          cat.stateTimer = Math.max(cat.stateTimer, 1.4);
          
          if (oncomingCar) {
            // Kat draait kopje/blik nieuwsgierig richting de naderende auto
            const angleToCar = Math.atan2(oncomingCar.y - cat.y, oncomingCar.x - cat.x);
            let diffA = angleToCar - cat.angle;
            while (diffA < -Math.PI) diffA += Math.PI * 2;
            while (diffA > Math.PI) diffA -= Math.PI * 2;
            cat.angle += diffA * Math.min(1, dt * 3.0);
          }

          if (Math.random() < 0.04 && (!cat.speechText || cat.speechTimer <= 0)) {
            cat.speechText = t(`chat.cat.wait${1 + Math.floor(Math.random() * 4)}`);
            cat.speechTimer = 2.0;
          }
        } else if (cat.stateTimer <= 0) {
          // Weg is helemaal veilig en vrij: steek nu alert over!
          cat.state = 'crossing';
          cat.speechText = t('chat.cat.cross');
          cat.speechTimer = 2.2;
          if (cat.crossTarget) {
            cat.targetX = cat.crossTarget.x;
            cat.targetY = cat.crossTarget.y;
          }
        }
      } else if (cat.state === 'sniffing' || cat.state === 'sitting' || cat.state === 'stretching') {
        cat.stateTimer -= dt;
        cat.tailPhase = (cat.tailPhase || 0) + dt * 2.5;
        if (cat.stateTimer <= 0) {
          cat.state = 'walking';
          this.pickNewCatTarget(cat);
        }
      } else {
        // State: 'walking' (op het trottoir) of 'crossing' (oversteken over de rijbaan)
        if (!cat.targetX || !cat.targetY) {
          this.pickNewCatTarget(cat);
        }

        const dx = cat.targetX - cat.x;
        const dy = cat.targetY - cat.y;
        const dist = Math.hypot(dx, dy);

        if (dist < 26) {
          if (cat.state === 'crossing') {
            // Oversteek geslaagd: kat staat veilig op de overkant van de straat
            cat.state = 'walking';
            cat.crossTarget = null;
            cat.speechText = t('chat.cat.crossed');
            cat.speechTimer = 2.0;
            this.pickNewCatTarget(cat);
          } else {
            // Bij elk punt blijft de kat even: snuffelen, zitten of rekken. Pas daarna loopt ze verder.
            const roll = Math.random();
            if (roll < 0.25) {
              cat.state = 'sniffing';
              cat.stateTimer = 4.0 + Math.random() * 3.0;
              cat.speechText = t('chat.cat.sniff');
              cat.speechTimer = 2.4;
            } else if (roll < 0.7) {
              cat.state = 'sitting';
              cat.stateTimer = 8.0 + Math.random() * 8.0;
              cat.speechText = (Math.random() < 0.5) ? t('chat.cat.wash') : t('chat.cat.purr');
              cat.speechTimer = 2.6;
            } else {
              cat.state = 'stretching';
              cat.stateTimer = 3.0 + Math.random() * 2.0;
              cat.speechText = t('chat.cat.stretch');
              cat.speechTimer = 2.2;
            }
          }
        } else {
          const targetAngle = Math.atan2(dy, dx);
          let diff = targetAngle - cat.angle;
          while (diff < -Math.PI) diff += Math.PI * 2;
          while (diff > Math.PI) diff -= Math.PI * 2;
          cat.angle += diff * Math.min(1, dt * 4.5);

          // Overstekende kat wandelt kwiek en alert door
          const speedMultiplier = (cat.state === 'crossing') ? 1.45 : 1.0;
          const step = (cat.speed || 1.05) * CAT_WALK_FACTOR * speedMultiplier * 60 * dt;
          let nextX = cat.x + Math.cos(cat.angle) * step;
          let nextY = cat.y + Math.sin(cat.angle) * step;

          // Botsingcontrole: Gebouwen EN stilstaande auto's (kat loopt nooit tegen auto's of door muren)
          const carCollision = this.checkStationaryCarCollision(nextX, nextY, 14);
          const bldgCollision = this.checkBuildingCollision(nextX, nextY, 10);

          if (carCollision) {
            // Kat detecteert stilstaande auto en stuurt er netjes omheen via de flank
            const avoidanceAngle = cat.angle + Math.PI * 0.45;
            nextX = cat.x + Math.cos(avoidanceAngle) * step;
            nextY = cat.y + Math.sin(avoidanceAngle) * step;
            if (this.checkStationaryCarCollision(nextX, nextY, 14)) {
              cat.angle += Math.PI * 0.8;
              this.pickNewCatTarget(cat);
            } else {
              cat.x = Math.max(60, Math.min(mapMaxX, nextX));
              cat.y = Math.max(60, Math.min(mapMaxY, nextY));
            }
          } else if (bldgCollision) {
            cat.angle += (Math.random() < 0.5 ? 1 : -1) * Math.PI * 0.4;
            this.pickNewCatTarget(cat);
          } else {
            cat.x = Math.max(60, Math.min(mapMaxX, nextX));
            cat.y = Math.max(60, Math.min(mapMaxY, nextY));
          }

          cat.walkCycle = (cat.walkCycle || 0) + step * 0.18;
          cat.tailPhase = (cat.tailPhase || 0) + dt * 4.5;
        }
      }
    }

    // 4. Stadsverkeer & AI-Verkeerssysteem: Spawnen, verkeersregels, stoplichten & interactie met speler
    this.manageCarSpawning(dt);
    this.updateCars(dt, soundEngine, scooter);

    // 5. Gouden Muntjes animatie & respawn timer
    if (this.collectibleCoins) {
      for (const coin of this.collectibleCoins) {
        if (coin.collected) {
          coin.respawnTimer -= dt;
          if (coin.respawnTimer <= 0) {
            coin.collected = false;
          }
        } else {
          coin.animTimer = (coin.animTimer + dt * 3.2) % (Math.PI * 2);
        }
      }
    }

    // 6. Nederlandse Grachten & Openstaande Ophaalbrug update
    this.updateCanalsAndBridges(dt, soundEngine, scooter);

    // 7. Wielrennerspeloton & Bakfietsmoeders update
    this.updateCyclists(dt, soundEngine, scooter);
  }

  // AI-Verkeerssysteem: Spawnt een nieuwe AI-auto op een geschikte stadsader
  spawnAiCar(customConfig = null) {
    if (!this.cars) this.cars = [];
    if (this.cars.length >= 26) return null;

    const corridors = [
      // Horizontaal Oostwaarts (Y = roadCenter + 50)
      { road: 'Noorderlaan', angle: 0, laneY: 470, startX: -80, axis: 'h' },
      { road: 'Hoofdstraat', angle: 0, laneY: 1250, startX: -80, axis: 'h' },
      { road: 'Singelweg', angle: 0, laneY: 1970, startX: -80, axis: 'h' },
      { road: 'Zuiderboulevard', angle: 0, laneY: 2790, startX: -80, axis: 'h' },
      { road: 'Industrieweg', angle: 0, laneY: 3570, startX: -80, axis: 'h' },
      { road: 'Havenring Zuid', angle: 0, laneY: 4370, startX: -80, axis: 'h' },
      // Horizontaal Westwaarts (Y = roadCenter - 50)
      { road: 'Noorderlaan', angle: Math.PI, laneY: 370, startX: this.width + 80, axis: 'h' },
      { road: 'Hoofdstraat', angle: Math.PI, laneY: 1150, startX: this.width + 80, axis: 'h' },
      { road: 'Singelweg', angle: Math.PI, laneY: 1870, startX: this.width + 80, axis: 'h' },
      { road: 'Zuiderboulevard', angle: Math.PI, laneY: 2690, startX: this.width + 80, axis: 'h' },
      { road: 'Industrieweg', angle: Math.PI, laneY: 3470, startX: this.width + 80, axis: 'h' },
      { road: 'Havenring Zuid', angle: Math.PI, laneY: 4270, startX: this.width + 80, axis: 'h' },
      // Verticaal Zuidwaarts (X = roadCenter - 50)
      { road: 'Klinkerstraat', angle: Math.PI / 2, laneX: 370, startY: -80, axis: 'v' },
      { road: 'Bakkerijboulevard', angle: Math.PI / 2, laneX: 1150, startY: -80, axis: 'v' },
      { road: 'Oostwal', angle: Math.PI / 2, laneX: 1870, startY: -80, axis: 'v' },
      { road: 'Havenboulevard', angle: Math.PI / 2, laneX: 2690, startY: -80, axis: 'v' },
      { road: 'Universiteitslaan', angle: Math.PI / 2, laneX: 3470, startY: -80, axis: 'v' },
      { road: 'Oosterring', angle: Math.PI / 2, laneX: 4270, startY: -80, axis: 'v' },
      // Verticaal Noordwaarts (X = roadCenter + 50)
      { road: 'Klinkerstraat', angle: -Math.PI / 2, laneX: 470, startY: this.height + 80, axis: 'v' },
      { road: 'Bakkerijboulevard', angle: -Math.PI / 2, laneX: 1250, startY: this.height + 80, axis: 'v' },
      { road: 'Oostwal', angle: -Math.PI / 2, laneX: 1970, startY: this.height + 80, axis: 'v' },
      { road: 'Havenboulevard', angle: -Math.PI / 2, laneX: 2790, startY: this.height + 80, axis: 'v' },
      { road: 'Universiteitslaan', angle: -Math.PI / 2, laneX: 3570, startY: this.height + 80, axis: 'v' },
      { road: 'Oosterring', angle: -Math.PI / 2, laneX: 4370, startY: this.height + 80, axis: 'v' }
    ];

    // Selecteer een corridor die niet geblokkeerd is bij het invoegpunt
    const shuffledCorridors = corridors.slice().sort(() => Math.random() - 0.5);
    let chosenCorridor = null;
    let spawnX = 0;
    let spawnY = 0;

    for (const corr of shuffledCorridors) {
      if (corr.axis === 'h') {
        spawnX = corr.startX;
        spawnY = corr.laneY;
        const blocked = this.cars.some(c => Math.abs((c.laneY || c.y) - corr.laneY) < 15 && Math.abs(c.x - spawnX) < 160);
        if (!blocked) { chosenCorridor = corr; break; }
      } else {
        spawnX = corr.laneX;
        spawnY = corr.startY;
        const blocked = this.cars.some(c => Math.abs((c.laneX || c.x) - corr.laneX) < 15 && Math.abs(c.y - spawnY) < 160);
        if (!blocked) { chosenCorridor = corr; break; }
      }
    }

    if (!chosenCorridor) return null;

    const carModels = [
      { model: 'sedan', length: 74, width: 35, colors: ['#f8fafc', '#1e293b', '#2563eb', '#059669', '#475569'], names: ['Sedan', 'Luxe Sedan', 'Zakenauto'] },
      { model: 'compact', length: 62, width: 33, colors: ['#dc2626', '#ea580c', '#eab308', '#9333ea', '#e11d48'], names: ['Stadsauto', 'Compact', 'Hatchback'] },
      { model: 'suv', length: 76, width: 38, colors: ['#16a34a', '#334155', '#0f766e', '#1e3a8a'], names: ['Grote SUV', 'Terreinwagen', 'Familie-SUV'] },
      { model: 'station', length: 76, width: 36, colors: ['#2563eb', '#059669', '#64748b', '#7c3aed'], names: ['Stationwagen', 'Combi', 'Gezinswagen'] },
      { model: 'cabrio', length: 58, width: 32, colors: ['#0891b2', '#e11d48', '#f59e0b'], names: ['Cabriolet', 'Sport-Cabrio'] },
      { model: 'taxi', length: 74, width: 35, colors: ['#f59e0b'], names: ['Dorps-Taxi', 'Stadstaxi'] },
      { model: 'electric', length: 72, width: 36, colors: ['#0284c7', '#38bdf8'], names: ['Elektrische Sedan', 'EV-Cruiser'] },
      { model: 'van', length: 80, width: 40, colors: ['#1e3a8a', '#475569', '#334155'], names: ['Bestelbus', 'Koeriersbus'] }
    ];

    const template = carModels[Math.floor(Math.random() * carModels.length)];
    const chosenColor = template.colors[Math.floor(Math.random() * template.colors.length)];
    const chosenName = template.names[Math.floor(Math.random() * template.names.length)];
    const baseSpeed = 1.6 + Math.random() * 0.55;

    this.carIdCounter = (this.carIdCounter || 1) + 1;
    const newCar = {
      id: `ai_car_${this.carIdCounter}`,
      name: chosenName,
      road: chosenCorridor.road,
      x: spawnX,
      y: spawnY,
      laneX: chosenCorridor.axis === 'v' ? chosenCorridor.laneX : undefined,
      laneY: chosenCorridor.axis === 'h' ? chosenCorridor.laneY : undefined,
      angle: chosenCorridor.angle,
      speed: baseSpeed * 0.8,
      targetSpeed: baseSpeed,
      length: template.length,
      width: template.width,
      color: chosenColor,
      roofColor: chosenColor,
      model: template.model,
      isBraking: false,
      hornCooldown: 0,
      flashTimer: 0,
      hazardTimer: 0,
      hazardBlink: false,
      turnSignal: null,
      turnTimer: 0,
      speechText: '',
      speechTimer: 0,
      playerBlockTimer: 0,
      reactionDelayTimer: 0,
      turnIntent: null
    };

    if (customConfig) {
      Object.assign(newCar, customConfig);
    }

    this.cars.push(newCar);
    return newCar;
  }

  // Beheert dynamische populatie van AI-auto's
  manageCarSpawning(dt) {
    this.carSpawnTimer = (this.carSpawnTimer || 0) + dt;
    const currentCount = (this.cars ? this.cars.length : 0);
    const target = this.targetCarCount || 22;

    // Zorg voor permanente aanwezigheid van Wijkpolitie patrouilles (minstens 2 eenheden)
    if (this.cars) {
      const policeCount = this.cars.filter(c => c.isPolice).length;
      if (policeCount < 2 && this.carSpawnTimer > 1.2) {
        this.carSpawnTimer = 0;
        this.spawnAiCar({
          isPolice: true,
          name: `Politiepatrouille 0${policeCount + 1} (Wijkagent Bromsnor)`,
          model: 'police',
          color: '#ffffff',
          roofColor: '#ffffff',
          sirenActive: false,
          sirenTimer: 0,
          state: 'patrol',
          targetSpeed: 2.1,
          speed: 2.1
        });
        return;
      }
    }

    if (currentCount < target && this.carSpawnTimer > 1.8) {
      this.carSpawnTimer = 0;
      this.spawnAiCar();
    }
  }

  // Reactie van nabijgelegen AI-auto's wanneer de speler toetert ('H')
  checkHornReactionToCars(scooter, soundEngine) {
    if (!this.cars || !scooter) return;

    for (const car of this.cars) {
      const dx = car.x - scooter.x;
      const dy = car.y - scooter.y;
      const dist = Math.hypot(dx, dy);

      if (dist < 165 && car.speechTimer <= 0) {
        // Vriendelijke toet-respons
        if (soundEngine && soundEngine.playFriendlyDoubleHorn) {
          soundEngine.playFriendlyDoubleHorn();
        } else if (soundEngine && soundEngine.playTrafficCarHorn) {
          soundEngine.playTrafficCarHorn();
        }

        car.flashTimer = 0.4;
        car.speechText = t(`chat.car${1 + Math.floor(Math.random() * 5)}`);
        car.speechTimer = 2.8;
        break; // Één reactie per toeter
      }
    }
  }

  // Volledige AI Verkeersregels, Stoplichten, Afstand houden & Speler Interactie
  updateCars(dt, soundEngine, scooter = null) {
    if (!this.cars) return;

    // Zorg voor de 6x6 kruispunten grid
    if (!this.intersections || this.intersections.length === 0) {
      this.intersections = [];
      const gridXs = [420, 1200, 1920, 2740, 3520, 4320];
      const gridYs = [420, 1200, 1920, 2740, 3520, 4320];
      for (const iy of gridYs) {
        for (const ix of gridXs) {
          const isAvenue = (ix === 1200 || ix === 2740 || iy === 1200 || iy === 2740);
          this.intersections.push({
            id: `int_${ix}_${iy}`,
            x: ix,
            y: iy,
            hw: 85,
            hh: 85,
            hasTrafficLight: isAvenue,
            trafficLight: isAvenue ? {
              timer: ((ix * 7 + iy * 11) % 160) / 10,
              cycleDuration: 16.0,
              horizontal: 'green',
              vertical: 'red'
            } : null
          });
        }
      }
    }

    const bridge = (this.drawBridges && this.drawBridges[0]) ? this.drawBridges[0] : null;

    for (let i = this.cars.length - 1; i >= 0; i--) {
      const car = this.cars[i];

      // Timers updaten
      if (car.speechTimer > 0) {
        car.speechTimer -= dt;
        if (car.speechTimer <= 0) car.speechText = '';
      }
      if (car.hazardTimer > 0) {
        car.hazardTimer -= dt;
        car.hazardBlink = Math.floor(Date.now() / 240) % 2 === 0;
      } else {
        car.hazardBlink = false;
      }
      if (car.turnTimer > 0) {
        car.turnTimer -= dt;
      }
      if (car.hornCooldown > 0) car.hornCooldown -= dt;
      if (car.flashTimer > 0) car.flashTimer -= dt;

      // Politievoertuig speciale logica (Achtervolging of Patrouille)
      if (car.isPolice) {
        if (car.sirenActive) car.sirenTimer = (car.sirenTimer || 0) + dt * 14;

        if (car.state === 'chase' && scooter) {
          const dxP = scooter.x - car.x;
          const dyP = scooter.y - car.y;
          const distP = Math.hypot(dxP, dyP);
          const directAngle = Math.atan2(dyP, dxP);

          // Slimme obstakel-ontwijking: controleer of de directe koers een gebouw raakt
          let bestAngle = directAngle;
          const forwardCheckDist = 48;
          const forwardX = car.x + Math.cos(directAngle) * forwardCheckDist;
          const forwardY = car.y + Math.sin(directAngle) * forwardCheckDist;

          if (this.checkBuildingCollision(forwardX, forwardY, 20)) {
            // Test alternatieve navigatiehoeken om soepel om gebouwen heen te sturen
            const testOffsets = [0.55, -0.55, 1.1, -1.1, 1.57, -1.57];
            for (const off of testOffsets) {
              const probeAngle = directAngle + off;
              const probeX = car.x + Math.cos(probeAngle) * forwardCheckDist;
              const probeY = car.y + Math.sin(probeAngle) * forwardCheckDist;
              if (!this.checkBuildingCollision(probeX, probeY, 20)) {
                bestAngle = probeAngle;
                break;
              }
            }
          }

          let diffA = bestAngle - car.angle;
          while (diffA < -Math.PI) diffA += Math.PI * 2;
          while (diffA > Math.PI) diffA -= Math.PI * 2;
          car.angle += diffA * Math.min(1, dt * 6.5);

          // Achtervolgingssnelheid: iets sneller dan jij, maar de politie rijdt je nooit omver. Vlak voor je remt
          // hij af en blijft op afstand staan; dat is het moment van de staandehouding.
          const chaseSpeed = Math.min(4.8, Math.max(3.6, Math.abs(scooter.speed) + 0.7));
          let targetChaseSpeed = chaseSpeed;
          // Dichtbij: je snelheid evenaren op de standaardafstand. Rijd je door, dan hangt hij op 105 px achter je
          // (staandehouding); sta je stil, dan komt hij tot stilstand.
          if (distP < POLICE_STAND_OFF + 130) {
            targetChaseSpeed = Math.max(0, Math.min(chaseSpeed, Math.abs(scooter.speed) + (distP - POLICE_STAND_OFF) * 0.035));
          }
          if (car.speed > targetChaseSpeed) car.speed = Math.max(targetChaseSpeed, car.speed - dt * 9);
          else car.speed = Math.min(targetChaseSpeed, car.speed + dt * 4.5);
          car.x += Math.cos(car.angle) * car.speed;
          car.y += Math.sin(car.angle) * car.speed;
          this.resolveVanBuildingCollision(car);

          // Megafoon roepen als de politie dichtbij is
          if (distP < 240 && (!car.speechText || car.speechTimer <= 0)) {
            car.speechText = t(`chat.police${1 + Math.floor(Math.random() * 4)}`);
            car.speechTimer = 3.2;
          }

          // Staandehouding: zodra de politie op afstand bij je is, nooit pas als hij je raakt
          if (distP < POLICE_STAND_OFF + 20 && this.onPoliceIntercept) {
            this.onPoliceIntercept(car);
          }
          continue;
        } else if (car.state === 'stopped') {
          car.speed = 0;
          continue;
        }
      }

      let desiredSpeed = car.targetSpeed || 1.8;
      const fx = Math.cos(car.angle);
      const fy = Math.sin(car.angle);

      // ==========================================
      // A. SPELER INTERACTIE & NOODSTOP / TOETER
      // ==========================================
      if (scooter) {
        const dxS = scooter.x - car.x;
        const dyS = scooter.y - car.y;
        const forwardDistS = dxS * fx + dyS * fy;
        const lateralDistS = Math.abs(-dxS * fy + dyS * fx);

        if (forwardDistS > 0 && forwardDistS < 140 && lateralDistS < (car.width / 2 + 22)) {
          // Remmen voor de scooter van de speler
          if (forwardDistS < 58) {
            desiredSpeed = 0;
          } else {
            desiredSpeed = Math.min(desiredSpeed, Math.max(0, ((forwardDistS - 45) / 85) * car.targetSpeed));
          }

          // Als de speler voor de auto blijft stilstaan: toeteren en knipperen!
          if (forwardDistS < 85 && Math.abs(scooter.speed) < 0.35) {
            car.playerBlockTimer = (car.playerBlockTimer || 0) + dt;
            if (car.playerBlockTimer > 1.2 && car.hornCooldown <= 0) {
              car.hornCooldown = 4.8;
              car.flashTimer = 0.7;
              if (soundEngine && soundEngine.playTrafficCarHorn) {
                soundEngine.playTrafficCarHorn();
              }
              const blockQuotes = [
                '🚗💢 "Doorrijden a.u.b.!"',
                '📢 "Het is groen hoor, bezorger!"',
                '🚙💨 "Aan de kant met die scooter!"',
                '🥐 "Die croissants koelen zo af!"',
                '⏰ "Ik moet naar m\'n werk!"'
              ];
              car.speechText = blockQuotes[Math.floor(Math.random() * blockQuotes.length)];
              car.speechTimer = 3.2;
            }
          } else {
            car.playerBlockTimer = Math.max(0, (car.playerBlockTimer || 0) - dt * 1.5);
          }
        }
      }

      // ==========================================
      // B. BUMPER-TO-BUMPER AFSTAND HOUDEN & REMMEN VOOR OVERSTEKENDE KATTEN
      // ==========================================
      // Remmen voor overstekende katten op de weg
      if (this.cats) {
        for (const cat of this.cats) {
          if (cat.dead || cat.state !== 'crossing') continue;
          const dxCat = cat.x - car.x;
          const dyCat = cat.y - car.y;
          const forwardDistCat = dxCat * fx + dyCat * fy;
          const lateralDistCat = Math.abs(-dxCat * fy + dyCat * fx);

          if (forwardDistCat > 0 && forwardDistCat < 140 && lateralDistCat < (car.width / 2 + 20)) {
            if (forwardDistCat < 50) {
              desiredSpeed = 0;
            } else {
              desiredSpeed = Math.min(desiredSpeed, Math.max(0, ((forwardDistCat - 40) / 90) * car.targetSpeed));
            }
          }
        }
      }

      for (let j = 0; j < this.cars.length; j++) {
        if (i === j) continue;
        const other = this.cars[j];
        const dirDot = fx * Math.cos(other.angle) + fy * Math.sin(other.angle);

        if (dirDot > 0.75) {
          const dx = other.x - car.x;
          const dy = other.y - car.y;
          const forwardDist = dx * fx + dy * fy;
          const lateralDist = Math.abs(-dx * fy + dy * fx);

          if (lateralDist < 28 && forwardDist > 0 && forwardDist < 190) {
            const minSafeGap = (car.length + other.length) / 2 + 20;
            if (forwardDist < minSafeGap) {
              desiredSpeed = 0;
            } else if (forwardDist < minSafeGap + 65) {
              const factor = (forwardDist - minSafeGap) / 65;
              desiredSpeed = Math.min(desiredSpeed, other.speed * factor);
            } else {
              desiredSpeed = Math.min(desiredSpeed, other.speed);
            }
          }
        }
      }

      // ==========================================
      // C. STOPLICHTEN & VOORRANG OP KRUISPUNTEN
      // ==========================================
      for (const inter of this.intersections) {
        let distToEntry = -1;
        let inLaneApproaching = false;
        let carAxis = 'horizontal';

        if (Math.abs(car.angle) < 0.08) { // Oostwaarts
          if (Math.abs(car.y - inter.y) <= inter.hh + 8 && car.x < inter.x - inter.hw) {
            distToEntry = (inter.x - inter.hw) - (car.x + car.length / 2);
            inLaneApproaching = true;
            carAxis = 'horizontal';
          }
        } else if (Math.abs(car.angle - Math.PI) < 0.08) { // Westwaarts
          if (Math.abs(car.y - inter.y) <= inter.hh + 8 && car.x > inter.x + inter.hw) {
            distToEntry = (car.x - car.length / 2) - (inter.x + inter.hw);
            inLaneApproaching = true;
            carAxis = 'horizontal';
          }
        } else if (Math.abs(car.angle - Math.PI / 2) < 0.08) { // Zuidwaarts
          if (Math.abs(car.x - inter.x) <= inter.hw + 8 && car.y < inter.y - inter.hh) {
            distToEntry = (inter.y - inter.hh) - (car.y + car.length / 2);
            inLaneApproaching = true;
            carAxis = 'vertical';
          }
        } else if (Math.abs(car.angle - (-Math.PI / 2)) < 0.08) { // Noordwaarts
          if (Math.abs(car.x - inter.x) <= inter.hw + 8 && car.y > inter.y + inter.hh) {
            distToEntry = (car.y - car.length / 2) - (inter.y + inter.hh);
            inLaneApproaching = true;
            carAxis = 'vertical';
          }
        }

        if (inLaneApproaching && distToEntry >= 0 && distToEntry < 95) {
          if (inter.hasTrafficLight && inter.trafficLight) {
            const signal = (carAxis === 'horizontal') ? inter.trafficLight.horizontal : inter.trafficLight.vertical;
            if (signal === 'red' || (signal === 'orange' && distToEntry > 24)) {
              desiredSpeed = Math.min(desiredSpeed, Math.max(0, (distToEntry / 90) * (car.targetSpeed || 1.8)));
              if (distToEntry < 14) desiredSpeed = 0;
              car.reactionDelayTimer = 0.35 + Math.random() * 0.25;
            } else if (signal === 'green') {
              if (car.reactionDelayTimer > 0) {
                car.reactionDelayTimer -= dt;
                desiredSpeed = Math.min(desiredSpeed, 0.4);
              }
            }
          } else {
            // Ongeregeld kruispunt: voorrang verlenen als kruispunt bezet is
            let junctionOccupied = false;
            for (let k = 0; k < this.cars.length; k++) {
              if (k === i) continue;
              const other = this.cars[k];
              const inJunction = Math.abs(other.x - inter.x) <= (inter.hw + 14) && Math.abs(other.y - inter.y) <= (inter.hh + 14);
              if (inJunction) {
                junctionOccupied = true;
                break;
              }
            }
            if (junctionOccupied) {
              desiredSpeed = Math.min(desiredSpeed, Math.max(0, (distToEntry / 75) * 0.6));
              if (distToEntry < 12) desiredSpeed = 0;
            }
          }
        }
      }

      // ==========================================
      // D. STOPPEN VOOR DE OPENSTAANDE OPHAALBRUG
      // ==========================================
      if (bridge && (bridge.openProgress > 0.05 || bridge.state === 'warning' || bridge.state === 'opening' || bridge.state === 'open')) {
        // Auto's op de Hoofdstraat (Y=1150 of Y=1250)
        if (Math.abs(car.y - 1250) < 30 && Math.abs(car.angle) < 0.1) {
          // Oostwaarts naderend naar Westelijke brugkade (X=2420)
          const distToBridge = (bridge.deckX - 25) - (car.x + car.length / 2);
          if (distToBridge >= 0 && distToBridge < 120) {
            desiredSpeed = Math.min(desiredSpeed, Math.max(0, (distToBridge / 110) * car.targetSpeed));
            if (distToBridge < 15) desiredSpeed = 0;
          }
        } else if (Math.abs(car.y - 1150) < 30 && Math.abs(car.angle - Math.PI) < 0.1) {
          // Westwaarts naderend naar Oostelijke brugkade (X=2540)
          const distToBridge = (car.x - car.length / 2) - (bridge.deckX + bridge.deckW + 25);
          if (distToBridge >= 0 && distToBridge < 120) {
            desiredSpeed = Math.min(desiredSpeed, Math.max(0, (distToBridge / 110) * car.targetSpeed));
            if (distToBridge < 15) desiredSpeed = 0;
          }
        }
      }

      // ==========================================
      // D2. STOPPEN VOOR EEN AFGESLOTEN STRAAT (marktdag)
      // ==========================================
      let blockedByClosure = false;
      for (const cl of this.closures || []) {
        // Afstand tot de afsluiting recht vooruit, en zit de auto in de rijstrook die erdoor loopt?
        const frontX = car.x + fx * (car.length / 2);
        const frontY = car.y + fy * (car.length / 2);
        let ahead = -1;
        if (Math.abs(fx) > 0.7 && frontY > cl.y - 6 && frontY < cl.y + cl.h + 6) ahead = fx > 0 ? cl.x - frontX : frontX - (cl.x + cl.w);
        else if (Math.abs(fy) > 0.7 && frontX > cl.x - 6 && frontX < cl.x + cl.w + 6) ahead = fy > 0 ? cl.y - frontY : frontY - (cl.y + cl.h);
        if (ahead >= -4 && ahead < 150) {
          desiredSpeed = Math.min(desiredSpeed, Math.max(0, ((ahead - 18) / 120) * car.targetSpeed));
          if (ahead < 24) desiredSpeed = 0;
          blockedByClosure = true;
        }
      }
      // Wie te lang voor een afsluiting staat, rijdt via een andere route verder (de auto wordt vervangen)
      car.closureWait = blockedByClosure && car.speed < 0.1 ? (car.closureWait || 0) + dt : 0;
      if (car.closureWait > 5 && !car.isPolice) {
        this.cars.splice(i, 1);
        this.spawnAiCar();
        continue;
      }

      // ==========================================
      // E. VLOEIEND ACCELEREREN / REMMEN
      // ==========================================
      if (car.speed > desiredSpeed) {
        car.speed = Math.max(desiredSpeed, car.speed - dt * 5.4);
        car.isBraking = true;
      } else {
        car.speed = Math.min(desiredSpeed, car.speed + dt * 2.1);
        car.isBraking = false;
      }

      // Positie bijwerken
      car.x += fx * car.speed;
      car.y += fy * car.speed;

      // Strikte rijbaanvergrendeling aan de rechterkant
      if (car.laneY !== undefined) car.y = car.laneY;
      if (car.laneX !== undefined) car.x = car.laneX;

      // ==========================================
      // F. DESPAWNING & WRAPPING AAN STADSRANDEN
      // ==========================================
      const maxX = this.width + 120;
      const maxY = this.height + 120;
      const minX = -120;
      const minY = -120;

      const isOutOfBounds = (
        (car.angle === 0 && car.x > maxX) ||
        (Math.abs(car.angle - Math.PI) < 0.1 && car.x < minX) ||
        (Math.abs(car.angle - Math.PI / 2) < 0.1 && car.y > maxY) ||
        (Math.abs(car.angle - (-Math.PI / 2)) < 0.1 && car.y < minY)
      );

      if (isOutOfBounds) {
        // Recycle car door direct een nieuwe in te laten voegen
        const wasPolice = car.isPolice;
        const oldState = car.state;
        this.cars.splice(i, 1);
        if (wasPolice) {
          this.spawnAiCar({
            isPolice: true,
            name: car.name || 'Politiepatrouille (Dienst Verkeer)',
            model: 'police',
            color: '#ffffff',
            roofColor: '#ffffff',
            sirenActive: (oldState === 'chase'),
            sirenTimer: 0,
            state: oldState || 'patrol',
            targetSpeed: (oldState === 'chase' ? 4.8 : 2.1),
            speed: (oldState === 'chase' ? 4.2 : 2.1)
          });
        } else {
          this.spawnAiCar();
        }
      }
    }

    // ==========================================
    // G. FYSIEKE ANTI-OVERLAP SOLVER
    // ==========================================
    for (let iter = 0; iter < 2; iter++) {
      for (let i = 0; i < this.cars.length; i++) {
        for (let j = i + 1; j < this.cars.length; j++) {
          const cA = this.cars[i];
          const cB = this.cars[j];
          const dx = cB.x - cA.x;
          const dy = cB.y - cA.y;
          const dist = Math.hypot(dx, dy);
          const minSafeDist = (cA.length + cB.length) / 2 + 8;

          if (dist < minSafeDist && dist > 0.001) {
            const overlap = minSafeDist - dist;
            const nx = dx / dist;
            const ny = dy / dist;

            cA.x -= nx * overlap * 0.5;
            cA.y -= ny * overlap * 0.5;
            cB.x += nx * overlap * 0.5;
            cB.y += ny * overlap * 0.5;

            if (cA.laneY !== undefined) cA.y = cA.laneY;
            if (cA.laneX !== undefined) cA.x = cA.laneX;
            if (cB.laneY !== undefined) cB.y = cB.laneY;
            if (cB.laneX !== undefined) cB.x = cB.laneX;

            cA.speed = 0;
            cB.speed = 0;
            cA.isBraking = true;
            cB.isBraking = true;
          }
        }
      }
    }
  }

  // Update van Grachten, Schepen en Hollandse Ophaalbrug "De Eendracht"
  updateCanalsAndBridges(dt, soundEngine, scooter) {
    if (!this.canals) return;

    const bridge = this.drawBridges ? this.drawBridges[0] : null;
    let anyShipApproaching = false;
    let anyShipInCrossing = false;

    // A. Schepen op de Prinsengracht (Vrachtschip De Volharding & Rondvaartboot De Krokante Zwaan)
    for (const canal of this.canals) {
      const boats = canal.boats || (canal.boat ? [canal.boat] : []);
      for (const b of boats) {
        if (b.hornCooldown > 0) b.hornCooldown -= dt;

        // Bepaal afstand tot ophaalbrug (Hoofdstraat, deckCenterY = 1200)
        const bridgeCenterY = bridge ? (bridge.deckY + bridge.deckH / 2) : 1200;
        const distToBridge = b.y - bridgeCenterY;
        const headingToBridge = (b.vy > 0 && distToBridge < -30) || (b.vy < 0 && distToBridge > 30);
        const inApproachZone = Math.abs(distToBridge) < 460 && headingToBridge;
        const inCrossingZone = Math.abs(distToBridge) < 160;

        if (inApproachZone || inCrossingZone) {
          anyShipApproaching = true;
          if (inCrossingZone) anyShipInCrossing = true;
        }

        // Als brug nog niet open is, wacht het naderende schip veilig vóór het brugdek
        const bridgeIsOpen = bridge && bridge.openProgress >= 0.88;
        const mustWait = inApproachZone && !bridgeIsOpen && Math.abs(distToBridge) < 140;

        if (mustWait) {
          b.isWaiting = true;
          b.speed = Math.max(0, (b.speed || b.targetSpeed || 1.0) - dt * 2.0);
        } else {
          b.isWaiting = false;
          b.speed = Math.min(b.targetSpeed || 1.0, (b.speed || 0.4) + dt * 1.5);
        }

        // Beweeg schip
        const actualVy = (b.vy >= 0 ? 1 : -1) * (b.speed || 0.85);
        b.y += actualVy * (dt * 60);

        // Keren bij uiteinden van de gracht
        if (b.y > b.maxY) {
          b.y = b.maxY;
          b.vy = -Math.abs(b.vy || -0.85);
          b.angle = -Math.PI / 2;
        } else if (b.y < b.minY) {
          b.y = b.minY;
          b.vy = Math.abs(b.vy || 0.85);
          b.angle = Math.PI / 2;
        }

        // Boeggolf & hekgolf waterdeeltjes
        if (!b.wakeParticles) b.wakeParticles = [];
        if (Math.random() < 0.45 && (b.speed || 0) > 0.15) {
          const sternY = b.y - Math.sin(b.angle) * (b.h / 2);
          const sternX = b.x - Math.cos(b.angle) * (b.h / 2);
          b.wakeParticles.push({
            x: sternX + (Math.random() - 0.5) * (b.w * 0.7),
            y: sternY,
            size: 2.2,
            alpha: 0.7,
            growth: 2.2
          });
        }
        for (let pIdx = b.wakeParticles.length - 1; pIdx >= 0; pIdx--) {
          const wp = b.wakeParticles[pIdx];
          wp.size += dt * 9.0;
          wp.alpha -= dt * 0.75;
          if (wp.alpha <= 0) b.wakeParticles.splice(pIdx, 1);
        }
      }
    }

    // B. Hollandse Ophaalbrug "De Eendracht" — State-Machine gestuurd door naderende scheepvaart
    if (this.drawBridges) {
      for (const br of this.drawBridges) {
        br.timer -= dt;

        if (br.state === 'closed') {
          br.openProgress = 0.0;
          br.barrierLowered = false;

          // Brug opent uitsluitend wanneer een groot schip de brug nadert!
          if (anyShipApproaching) {
            br.state = 'warning';
            br.timer = 3.8;
            br.barrierLowered = true;
            br.warningBellTimer = 0;
            if (soundEngine && soundEngine.playBridgeBell) soundEngine.playBridgeBell();
            if (soundEngine && soundEngine.playShipHorn) soundEngine.playShipHorn();
          }
        } else if (br.state === 'warning') {
          br.warningBellTimer = (br.warningBellTimer || 0) + dt;
          if (br.warningBellTimer > 0.7) {
            br.warningBellTimer = 0;
            if (soundEngine && soundEngine.playBridgeBell) soundEngine.playBridgeBell();
          }
          if (br.timer <= 0) {
            br.state = 'opening';
            br.timer = 4.5;
          }
        } else if (br.state === 'opening') {
          br.openProgress = Math.min(1.0, br.openProgress + (dt / 4.2));
          if (br.openProgress >= 1.0 || br.timer <= 0) {
            br.state = 'open';
            br.openProgress = 1.0;
            br.timer = 3.0; // Minimale wachttijd
          }
        } else if (br.state === 'open') {
          br.openProgress = 1.0;
          // Brug blijft open zolang er een schip in de oversteek- of naderingszone is
          if (!anyShipApproaching && !anyShipInCrossing && br.timer <= 0) {
            br.state = 'closing';
            br.timer = 4.5;
            if (soundEngine && soundEngine.playBridgeBell) soundEngine.playBridgeBell();
          }
        } else if (br.state === 'closing') {
          br.openProgress = Math.max(0.0, br.openProgress - (dt / 4.2));
          br.warningBellTimer = (br.warningBellTimer || 0) + dt;
          if (br.warningBellTimer > 0.8) {
            br.warningBellTimer = 0;
            if (soundEngine && soundEngine.playBridgeBell) soundEngine.playBridgeBell();
          }
          if (br.openProgress <= 0.0 || br.timer <= 0) {
            br.state = 'closed';
            br.openProgress = 0.0;
            br.barrierLowered = false;
            br.timer = 0;
          }
        }
      }
    }
  }

  // Update van het Wielrennerspeloton & Bakfietsmoeders
  updateCyclists(dt, soundEngine, scooter) {
    if (!this.cyclists) return;

    // Zoek ploegleider voor het peloton
    const leader = this.cyclists.find(c => c.type === 'peloton' && c.role === 'leader');
    if (leader && leader.waypoints) {
      // 1. Ploegleider navigeert over de route
      const wp = leader.waypoints[leader.waypointIdx];
      const dx = wp.x - leader.x;
      const dy = wp.y - leader.y;
      const dist = Math.hypot(dx, dy);

      if (dist < 45) {
        leader.waypointIdx = (leader.waypointIdx + 1) % leader.waypoints.length;
      } else {
        const targetAngle = Math.atan2(dy, dx);
        let diffA = targetAngle - leader.angle;
        while (diffA < -Math.PI) diffA += Math.PI * 2;
        while (diffA > Math.PI) diffA -= Math.PI * 2;
        leader.angle += diffA * Math.min(1, dt * 4.5);
      }

      leader.x += Math.cos(leader.angle) * leader.speed * (dt * 60);
      leader.y += Math.sin(leader.angle) * leader.speed * (dt * 60);
      leader.pedalPhase = (leader.pedalPhase + dt * 14) % (Math.PI * 2);

      // Fietsbel rinkelen bij passage van scooter
      if (scooter && leader.bellCooldown <= 0) {
        const dScooter = Math.hypot(scooter.x - leader.x, scooter.y - leader.y);
        if (dScooter < 75) {
          if (soundEngine && soundEngine.playBicycleBell) soundEngine.playBicycleBell();
          leader.bellCooldown = 4.0;
        }
      }
      if (leader.bellCooldown > 0) leader.bellCooldown -= dt;

      // 2. Peloton volgers volgen strak in het wiel van de voorligger
      const followers = this.cyclists.filter(c => c.type === 'peloton' && c.role === 'follower');
      let prevRider = leader;
      for (const follower of followers) {
        const targetDist = 58; // 58px achter de voorligger in waaier-formatie
        const fx = prevRider.x - Math.cos(prevRider.angle) * targetDist;
        const fy = prevRider.y - Math.sin(prevRider.angle) * targetDist;

        follower.x += (fx - follower.x) * Math.min(1, dt * 7.5);
        follower.y += (fy - follower.y) * Math.min(1, dt * 7.5);
        follower.angle = prevRider.angle;
        follower.pedalPhase = (follower.pedalPhase + dt * 14) % (Math.PI * 2);
        prevRider = follower;
      }
    }

    // 3. Bakfietsmoeders
    const bakfietsen = this.cyclists.filter(c => c.type === 'bakfiets');
    for (const bf of bakfietsen) {
      if (bf.waypoints && bf.waypoints.length > 0) {
        const wp = bf.waypoints[bf.waypointIdx || 0];
        const dx = wp.x - bf.x;
        const dy = wp.y - bf.y;
        const dist = Math.hypot(dx, dy);

        if (dist < 35) {
          bf.waypointIdx = ((bf.waypointIdx || 0) + 1) % bf.waypoints.length;
        } else {
          const targetAngle = Math.atan2(dy, dx);
          let diffA = targetAngle - bf.angle;
          while (diffA < -Math.PI) diffA += Math.PI * 2;
          while (diffA > Math.PI) diffA -= Math.PI * 2;
          bf.angle += diffA * Math.min(1, dt * 3.5);
        }

        bf.x += Math.cos(bf.angle) * bf.speed * (dt * 60);
        bf.y += Math.sin(bf.angle) * bf.speed * (dt * 60);
        bf.pedalPhase = (bf.pedalPhase + dt * 8) % (Math.PI * 2);

        // Gezellige fietsbel rinkelen bij nabijheid
        if (scooter && (bf.bellCooldown || 0) <= 0) {
          const dS = Math.hypot(scooter.x - bf.x, scooter.y - bf.y);
          if (dS < 65) {
            if (soundEngine && soundEngine.playBicycleBell) soundEngine.playBicycleBell();
            bf.bellCooldown = 5.0;
          }
        }
        if (bf.bellCooldown > 0) bf.bellCooldown -= dt;
      }
    }
  }

  // Nieuwe bestemming kiezen voor rondlopende kat in de wijk (voornamelijk stoepen, soms oversteken)
  pickNewCatTarget(cat) {
    const sidewalks = this.sidewalkWaypoints || this.neighborhoodWaypoints || [];
    if (sidewalks.length === 0) return;

    const player = this.playerRef;
    // Oversteken is zeldzaam: een kat steekt hooguit eens in de ~45 seconden over, en er zijn nooit meer dan twee tegelijk
    // onderweg. Ook niet als de scooter vlakbij het oversteekpunt staat.
    const mayCross = !(cat.crossCooldown > 0) && (this.crossingCats ?? 0) < MAX_CROSSING_CATS;
    const shouldCross = mayCross && Math.random() < 0.06 && this.streetCrossings && this.streetCrossings.length > 0;
    if (shouldCross) {
      // Zoek dichtstbijzijnde oversteekpunt (binnen 450px) dat niet vlak bij de speler ligt
      const nearbyCrossings = this.streetCrossings.filter(
        (c) => Math.hypot(c.from.x - cat.x, c.from.y - cat.y) < 450 && !(player && Math.hypot(c.from.x - player.x, c.from.y - player.y) < 220),
      );
      if (nearbyCrossings.length > 0) {
        const crossing = nearbyCrossings[Math.floor(Math.random() * nearbyCrossings.length)];
        // Loop eerst naar de stoeprand vóór de oversteek
        cat.targetX = crossing.from.x + (Math.random() - 0.5) * 16;
        cat.targetY = crossing.from.y + (Math.random() - 0.5) * 16;
        cat.crossTarget = { x: crossing.to.x + (Math.random() - 0.5) * 16, y: crossing.to.y + (Math.random() - 0.5) * 16 };
        cat.state = 'waiting_curb';
        cat.crossCooldown = 45 + Math.random() * 30;
        this.crossingCats = (this.crossingCats ?? 0) + 1;
        cat.stateTimer = 1.2 + Math.random() * 1.5;
        cat.speechText = '🐾 *Kijkt naar oversteekplaats*';
        cat.speechTimer = 2.0;
        return;
      }
    }

    // Standaard: Wandel over de stoep (kies een stoep-waypoint in de wijk)
    const valid = sidewalks.filter(wp => {
      const d = Math.hypot(wp.x - cat.x, wp.y - cat.y);
      if (!(d > 60 && d < 450)) return false;
      // Nooit een bestemming vlak bij de scooter
      return !(player && Math.hypot(wp.x - player.x, wp.y - player.y) < CAT_PERSONAL_SPACE + 40);
    });

    const chosen = valid.length > 0 
      ? valid[Math.floor(Math.random() * valid.length)] 
      : sidewalks[Math.floor(Math.random() * sidewalks.length)];

    cat.targetX = chosen.x + (Math.random() - 0.5) * 24;
    cat.targetY = chosen.y + (Math.random() - 0.5) * 24;
  }

  // Detectie van aanrijding met een dorpskat
  checkCatCollision(scooter) {
    if (!this.cats || !scooter) return null;
    for (const cat of this.cats) {
      if (cat.dead) continue;
      const dx = scooter.x - cat.x;
      const dy = scooter.y - cat.y;
      const dist = Math.hypot(dx, dy);
      // Contactstraal tussen scooter en kat. Een stilstaande scooter raakt geen kat: de kat stapt zelf opzij.
      if (dist < 26 && Math.abs(scooter.speed) > CAT_HIT_MIN_SPEED) {
        cat.dead = true;
        cat.state = 'dead';
        cat.deathTimer = 0;
        cat.ghostY = 0;
        cat.ghostAlpha = 1.0;
        return cat;
      }
    }
    return null;
  }

  // Fysieke botsingdetectie met stadsverkeer auto's EN stilstaande auto's in de file
  checkCarCollisions(scooter) {
    let hitResult = null;
    const allCars = [...(this.cars || [])];
    if (this.trafficJams) {
      for (const jam of this.trafficJams) {
        if (jam.active && jam.cars) {
          allCars.push(...jam.cars);
        }
      }
    }
    if (allCars.length === 0) return null;

    for (const car of allCars) {
      const dx = scooter.x - car.x;
      const dy = scooter.y - car.y;
      const cosA = Math.cos(-car.angle);
      const sinA = Math.sin(-car.angle);
      const localX = dx * cosA - dy * sinA;
      const localY = dx * sinA + dy * cosA;

      const halfL = car.length / 2 + 10;
      const halfW = car.width / 2 + 10;

      if (Math.abs(localX) < halfL && Math.abs(localY) < halfW) {
        const penX = halfL - Math.abs(localX);
        const penY = halfW - Math.abs(localY);

        if (penX < penY) {
          const pushX = localX > 0 ? penX : -penX;
          scooter.x += pushX * Math.cos(car.angle);
          scooter.y += pushX * Math.sin(car.angle);
        } else {
          const pushY = localY > 0 ? penY : -penY;
          scooter.x += pushY * -Math.sin(car.angle);
          scooter.y += pushY * Math.cos(car.angle);
        }

        const carSpd = car.speed || 0;
        const relativeSpeed = Math.abs(scooter.speed) + carSpd;
        scooter.speed *= 0.35;
        if (car.speed !== undefined) car.speed = Math.max(0, car.speed - 0.7);
        car.isBraking = true;
        car.hazardTimer = 4.5;
        car.flashTimer = 0.8;
        car.hornCooldown = 3.5;

        const angryQuotes = [
          '🚗💥 "Kijk uit je doppen, bezorger!"',
          '⚠️ "Mijn bumper! Schadeformulier?!"',
          '📢 "Pas toch op je spiegels!"',
          '💥 "Ho ho ho, remmen!"',
          '🚗💢 "Heb je je rijbewijs bij een pak boter gekregen?!"'
        ];
        car.speechText = angryQuotes[Math.floor(Math.random() * angryQuotes.length)];
        car.speechTimer = 3.6;

        // Als het een politiewagen is tijdens een achtervolging of alarm: direct staandehouding
        if (car.isPolice && (car.state === 'chase' || this.policeActive) && !this.policeSuppressed && this.onPoliceIntercept) {
          this.onPoliceIntercept(car);
        }

        hitResult = { hit: true, car, relativeSpeed };
        break;
      }
    }
    return hitResult;
  }

  // Check if vehicle touches or hits any traffic cone
  checkConeCollision(scooter) {
    let hitCone = null;
    const isVan = (scooter.vehicleType === 'van');
    const cosA = Math.cos(scooter.angle);
    const sinA = Math.sin(scooter.angle);
    const noseOffset = isVan ? 34 : 20;
    const hitRadius = isVan ? 20 : 14;
    const noseX = scooter.x + cosA * noseOffset;
    const noseY = scooter.y + sinA * noseOffset;

    for (const c of this.cones) {
      const dx = noseX - c.x;
      const dy = noseY - c.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < c.r + hitRadius) {
        c.knocked = true;
        const pushAngle = Math.atan2(dy, dx);
        const impulse = isVan ? Math.max(4.5, Math.abs(scooter.speed) * 2.8) : Math.max(2.5, Math.abs(scooter.speed) * 1.8);
        c.vx = -Math.cos(pushAngle) * impulse;
        c.vy = -Math.sin(pushAngle) * impulse;
        hitCone = c;
      }
    }
    return hitCone;
  }

  // Detecteer of speler door een actieve file manoeuvreert om tijd te winnen
  checkTrafficJamSlalom(scooter) {
    if (!this.trafficJams) return null;
    for (const jam of this.trafficJams) {
      if (!jam.active || jam.slalomCooldown > 0) continue;
      const inZone = (scooter.x >= jam.x && scooter.x <= jam.x + jam.w &&
                      scooter.y >= jam.y && scooter.y <= jam.y + jam.h);
      if (inZone && Math.abs(scooter.speed) > 1.2) {
        jam.slalomCooldown = 18.0; // Cooldown voor deze file
        return jam;
      }
    }
    return null;
  }

  // Update logic for active competitor Knus Expres van (movement, navigation & rivalry deliveries)
  updateVan(dt, soundEngine, scooter = null) {
    const v = this.van;
    if (!v) return;

    // A. Hazard blinker timer (blinks during stops and delivery drops)
    v.hazardTimer += dt;
    const blinkInterval = (v.state === 'delivering') ? 0.35 : 0.6;
    if (v.hazardTimer > blinkInterval) {
      v.hazardTimer = 0;
      v.hazardBlink = !v.hazardBlink;
    }

    // B. Speech bubble timer
    if (v.speechTimer > 0) {
      v.speechTimer -= dt;
      if (v.speechTimer <= 0) {
        v.speechText = '';
      }
    }

    // C. Horn cooldown timer
    if (v.honkCooldown > 0) {
      v.honkCooldown -= dt;
    }

    // D. Exhaust smoke particle engine
    if (!v.smokeParticles) v.smokeParticles = [];
    const smokeRate = (v.state === 'driving' ? 0.32 : 0.08);
    if (Math.random() < smokeRate) {
      const rearX = v.x - Math.cos(v.angle) * 44 + (Math.random() - 0.5) * 6;
      const rearY = v.y - Math.sin(v.angle) * 44 + (Math.random() - 0.5) * 6;
      v.smokeParticles.push({
        x: rearX,
        y: rearY,
        vx: -Math.cos(v.angle) * (0.3 + Math.random() * 0.4) + (Math.random() - 0.5) * 0.2,
        vy: -Math.sin(v.angle) * (0.3 + Math.random() * 0.4) - (0.2 + Math.random() * 0.3),
        size: 3.5 + Math.random() * 2.5,
        alpha: 0.6
      });
    }
    for (let i = v.smokeParticles.length - 1; i >= 0; i--) {
      const p = v.smokeParticles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.size += 0.12;
      p.alpha -= 0.016;
      if (p.alpha <= 0) {
        v.smokeParticles.splice(i, 1);
      }
    }

    // E. Scooter proximity detection & interactive reaction
    if (scooter) {
      const dxScoot = scooter.x - v.x;
      const dyScoot = scooter.y - v.y;
      const distToScoot = Math.sqrt(dxScoot * dxScoot + dyScoot * dyScoot);

      // Knus Expres honks & taunts when player is close or in the way
      if (distToScoot < 115 && v.honkCooldown <= 0) {
        v.honkCooldown = 5.5;
        if (soundEngine && soundEngine.playCarHorn) {
          soundEngine.playCarHorn();
        }
        const taunts = [
          '⚡ Sjors: "Aan de kant, Knus & Krokant! Knus Expres heeft haast!"',
          '⚡ Sjors: "Onze broodjes zijn sneller en heter geleverd!"',
          '⚡ Sjors: "Kijk uit voor onze bus, koerier!"',
          '⚡ Sjors: "Toet toet! Knus Expres rijdt op kop in het dorp!"'
        ];
        v.speechText = taunts[Math.floor(Math.random() * taunts.length)];
        v.speechTimer = 4.2;
      }
    }

    // F0. Race om dezelfde klant: Sjors volgt een eigen route (eerst langs zijn depot)
    if (v.race) {
      this.updateVanRace(v, scooter, soundEngine, dt);
      return;
    }

    // F. Waypoint Navigation & Delivery Cycle
    const wp = v.waypoints[v.waypointIndex];
    if (!wp) return;

    if (v.state === 'driving') {
      v.doorOpen = false;
      const dx = wp.x - v.x;
      const dy = wp.y - v.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Desired angle towards waypoint
      v.targetAngle = Math.atan2(dy, dx);

      // Angular smoothing
      let diff = v.targetAngle - v.angle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      v.angle += diff * 0.085;

      // Desired speed with collision avoidance for player
      let desSpeed = v.targetSpeed || 2.1;
      if (scooter) {
        const toScoot = Math.atan2(scooter.y - v.y, scooter.x - v.x);
        let relA = toScoot - v.angle;
        while (relA > Math.PI) relA -= Math.PI * 2;
        while (relA < -Math.PI) relA += Math.PI * 2;
        const dScoot = Math.sqrt((scooter.x - v.x) ** 2 + (scooter.y - v.y) ** 2);
        if (Math.abs(relA) < 0.7 && dScoot < 85) {
          desSpeed = 0.4; // Brake gently for player scooter
        }
      }

      // Smooth acceleration
      v.speed += (desSpeed - v.speed) * 0.08;
      v.x += Math.cos(v.angle) * v.speed;
      v.y += Math.sin(v.angle) * v.speed;

      // Ensure van never clips or penetrates buildings or barriers
      this.resolveVanBuildingCollision(v);

      // Reached waypoint? (Smooth corner radius vs precise delivery stop)
      const reachThreshold = wp.stop ? 28 : 38;
      if (dist < reachThreshold) {
        if (wp.stop) {
          const isStopActive = !v.activeStopIds || v.activeStopIds.length === 0 || v.activeStopIds.includes(wp.destId);
          if (isStopActive) {
            // Arrived at authentic delivery stop
            v.state = 'delivering';
            v.totalDuration = wp.duration || 5.0;
            v.stateTimer = v.totalDuration;
            v.currentStop = wp.name;
            v.currentAddress = wp.address || wp.name;
            v.currentProduct = wp.product || 'Bestelling';
            v.targetDoorX = wp.doorX || wp.x;
            v.targetDoorY = wp.doorY || wp.y;
            v.speed = 0;
            v.doorOpen = true;
            v.doorTimer = 0;
            v.hazardBlink = true;
            v.courierVisible = true;
            v.courierHasBox = true;
            v.packageDelivered = false;
            v.courierWalkCycle = 0;
            v.speechText = (wp.destId === 'depot')
              ? '⚡ Sjors: "Even nieuwe voorraad baksels inladen bij het depot!"'
              : `📦 Sjors: "Bestelling afgeven bij ${wp.name}!"`;
            v.speechTimer = 3.6;
            if (soundEngine && soundEngine.playUiClick) {
              soundEngine.playUiClick();
            }
          } else {
            // Skip stop this lap, drive on smoothly
            v.waypointIndex = (v.waypointIndex + 1) % v.waypoints.length;
            if (v.waypointIndex === 0) {
              v.lapCount = (v.lapCount || 0) + 1;
              this.rotateRivalStops(v);
            }
          }
        } else {
          // Intermediate street corner waypoint
          v.waypointIndex = (v.waypointIndex + 1) % v.waypoints.length;
          if (v.waypointIndex === 0) {
            v.lapCount = (v.lapCount || 0) + 1;
            this.rotateRivalStops(v);
          }
        }
      }
    } else if (v.state === 'delivering') {
      v.speed = 0;
      v.stateTimer -= dt;
      const totalDur = v.totalDuration || 5.0;
      const progress = Math.max(0, Math.min(1, 1.0 - (v.stateTimer / totalDur)));

      // Determine curbside door position relative to van heading
      const toDx = (v.targetDoorX || v.x) - v.x;
      const toDy = (v.targetDoorY || v.y) - v.y;
      const cross = Math.cos(v.angle) * toDy - Math.sin(v.angle) * toDx;
      const side = (cross >= 0) ? 1 : -1;
      const doorWorldX = v.x - Math.cos(v.angle) * 12 - Math.sin(v.angle) * (side * 24);
      const doorWorldY = v.y - Math.sin(v.angle) * 12 + Math.cos(v.angle) * (side * 24);

      if (progress < 0.42) {
        // Phase 1: Courier steps out and walks across sidewalk to customer door holding box
        const p = progress / 0.42;
        v.courierVisible = true;
        v.courierHasBox = true;
        v.doorOpen = true;
        v.courierX = doorWorldX + (v.targetDoorX - doorWorldX) * p;
        v.courierY = doorWorldY + (v.targetDoorY - doorWorldY) * p;
        v.courierAngle = Math.atan2(v.targetDoorY - doorWorldY, v.targetDoorX - doorWorldX);
        v.courierWalkCycle = (v.courierWalkCycle || 0) + dt * 12;
      } else if (progress < 0.58) {
        // Phase 2: At customer door ringing bell & handing over package
        v.courierVisible = true;
        v.courierX = v.targetDoorX;
        v.courierY = v.targetDoorY;
        v.courierWalkCycle = 0;
        if (!v.packageDelivered) {
          v.packageDelivered = true;
          v.courierHasBox = false;
          if (soundEngine && soundEngine.playChime) {
            soundEngine.playChime();
          }
          if (this.floatingTexts) {
            this.floatingTexts.push({
              x: v.targetDoorX,
              y: v.targetDoorY - 24,
              text: (wp.destId === 'depot') ? '🥖 Baksels Ingeladen!' : '📦 Bezorgd door Sjors!',
              alpha: 1.0,
              color: '#e11d48'
            });
          }
          v.speechText = (wp.destId === 'depot')
            ? '⚡ Sjors: "Verse voorraad geladen, snel weer de weg op!"'
            : `⚡ Sjors: "${v.currentProduct} afgegeven bij ${v.currentStop}!"`;
          v.speechTimer = 3.6;
        }
      } else if (progress < 0.90) {
        // Phase 3: Walking back from customer door to van empty-handed
        const p = (progress - 0.58) / 0.32;
        v.courierVisible = true;
        v.courierHasBox = false;
        v.doorOpen = true;
        v.courierX = v.targetDoorX + (doorWorldX - v.targetDoorX) * p;
        v.courierY = v.targetDoorY + (doorWorldY - v.targetDoorY) * p;
        v.courierAngle = Math.atan2(doorWorldY - v.targetDoorY, doorWorldX - v.targetDoorX);
        v.courierWalkCycle = (v.courierWalkCycle || 0) + dt * 12;
      } else {
        // Phase 4: Getting back in, closing door
        v.courierX = doorWorldX;
        v.courierY = doorWorldY;
        v.courierVisible = (progress < 0.95);
        v.doorOpen = (progress < 0.96);
      }

      if (v.stateTimer <= 0) {
        // Delivery completed!
        v.deliveries++;
        v.state = 'driving';
        v.doorOpen = false;
        v.courierVisible = false;
        v.hazardBlink = false;
        v.waypointIndex = (v.waypointIndex + 1) % v.waypoints.length;
        if (v.waypointIndex === 0) {
          v.lapCount = (v.lapCount || 0) + 1;
          this.rotateRivalStops(v);
        }
        v.speechText = (wp.destId === 'depot')
          ? '⚡ Sjors: "Bus volgeladen! Knus Expres bezorgt sneller dan het licht!"'
          : `⚡ Sjors: "Bestelling #${v.deliveries} bezorgd bij ${v.currentStop}! Knus Expres blijft de snelste!"`;
        v.speechTimer = 4.0;
        if (soundEngine && soundEngine.playCarHorn) {
          soundEngine.playCarHorn();
        }
        if (this.onRivalDelivery) {
          this.onRivalDelivery(v);
        }
      }
    }
  }

  // Rotate Knus Expres active delivery destinations across successive laps
  /**
   * Start een race: Sjors rijdt over `route` (lijst punten) naar de klant. Bij `loadAt` (index in de route)
   * staat hij even stil om in te laden, net als de speler bij de bakkerij.
   */
  startRivalRace(destId, route, loadAt = -1, loadSeconds = 2.5) {
    const v = this.van;
    if (!v) return;
    v.race = { destId, route, i: 0, loadAt, loadTimer: 0, loadSeconds };
    v.state = 'driving';
    v.doorOpen = false;
    v.courierVisible = false;
    v.speechText = '⚡ Sjors: "Die klant is van mij! Knus Expres is er eerst!"';
    v.speechTimer = 3.5;
  }

  /** Breekt een lopende race af (bijvoorbeeld omdat de speler al bezorgd heeft). */
  stopRivalRace(won) {
    const v = this.van;
    if (!v?.race) return;
    v.race = null;
    v.speechText = won ? '⚡ Sjors: "Hè! Volgende keer ben ik er eerst!"' : '⚡ Sjors: "Ha! Knus Expres wint weer!"';
    v.speechTimer = 3.5;
    // Terug naar het dichtstbijzijnde punt van zijn vaste lus
    let best = 0;
    let bestD = Infinity;
    v.waypoints.forEach((w, i) => {
      const d = Math.hypot(w.x - v.x, w.y - v.y);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    v.waypointIndex = best;
  }

  updateVanRace(v, scooter, soundEngine, dt) {
    const r = v.race;
    // Inladen bij het depot
    if (r.i === r.loadAt && r.loadTimer < r.loadSeconds) {
      const p = r.route[r.i];
      if (Math.hypot(p.x - v.x, p.y - v.y) < 40) {
        v.speed = 0;
        v.hazardBlink = true;
        r.loadTimer += dt;
        if (r.loadTimer >= r.loadSeconds) r.i++;
        return;
      }
    }
    const wp = r.route[r.i];
    const dx = wp.x - v.x;
    const dy = wp.y - v.y;
    const dist = Math.hypot(dx, dy);
    let diff = Math.atan2(dy, dx) - v.angle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    v.angle += diff * 0.1;

    // In de race iets sneller dan normaal, maar remmen voor de speler en in scherpe bochten
    let desSpeed = Math.abs(diff) > 0.8 ? 1.2 : 2.6;
    if (scooter) {
      let rel = Math.atan2(scooter.y - v.y, scooter.x - v.x) - v.angle;
      while (rel > Math.PI) rel -= Math.PI * 2;
      while (rel < -Math.PI) rel += Math.PI * 2;
      if (Math.abs(rel) < 0.7 && Math.hypot(scooter.x - v.x, scooter.y - v.y) < 85) desSpeed = 0.4;
    }
    v.speed += (desSpeed - v.speed) * 0.08;
    v.x += Math.cos(v.angle) * v.speed;
    v.y += Math.sin(v.angle) * v.speed;
    this.resolveVanBuildingCollision(v);

    if (dist < (r.i === r.route.length - 1 ? 50 : 36)) {
      if (r.i === r.loadAt && r.loadTimer < r.loadSeconds) return;
      r.i++;
      if (r.i >= r.route.length) {
        const destId = r.destId;
        this.stopRivalRace(false);
        if (soundEngine?.playCarHorn) soundEngine.playCarHorn();
        this.onRivalRaceArrive?.(destId);
      }
    }
  }

  rotateRivalStops(v) {
    const schedules = [
      ['tandarts', 'anker', 'rozenhof', 'student', 'oost'],
      ['bloem', 'horizon', 'jansen', 'vandijk', 'depot'],
      ['anker', 'horizon', 'student', 'oost', 'vandijk'],
      ['rozenhof', 'jansen', 'tandarts', 'bloem', 'depot']
    ];
    const idx = (v.lapCount || 0) % schedules.length;
    v.activeStopIds = schedules[idx];
  }

  // Dynamic Rotated Bounding Box Physical Collision with Knus Expres Van
  checkVanCollision(scooter) {
    const v = this.van;
    if (!v) return null;

    // Vector from van center to scooter
    const dx = scooter.x - v.x;
    const dy = scooter.y - v.y;

    // Transform into van's local rotated coordinate space
    const cosA = Math.cos(-v.angle);
    const sinA = Math.sin(-v.angle);
    const localX = dx * cosA - dy * sinA;
    const localY = dx * sinA + dy * cosA;

    // Van dimensions (w=96, h=46) with scooter chassis radius buffer (~14px)
    const halfW = (v.w / 2) + 14;
    const halfH = (v.h / 2) + 14;

    if (Math.abs(localX) < halfW && Math.abs(localY) < halfH) {
      // Collision detected! Push scooter out
      const overlapX = halfW - Math.abs(localX);
      const overlapY = halfH - Math.abs(localY);

      let pushLocalX = 0;
      let pushLocalY = 0;
      if (overlapX < overlapY) {
        pushLocalX = localX > 0 ? overlapX : -overlapX;
      } else {
        pushLocalY = localY > 0 ? overlapY : -overlapY;
      }

      // Convert push back to world coordinates
      const worldPushX = pushLocalX * Math.cos(v.angle) - pushLocalY * Math.sin(v.angle);
      const worldPushY = pushLocalX * Math.sin(v.angle) + pushLocalY * Math.cos(v.angle);

      // Repel scooter outward
      scooter.x += worldPushX * 1.15;
      scooter.y += worldPushY * 1.15;

      const relativeSpeed = Math.abs(scooter.speed) + Math.abs(v.speed);
      scooter.speed *= -0.45;

      if (v.honkCooldown <= 0) {
        v.honkCooldown = 3.5;
        v.speechText = '⚡ Sjors: "Hé! Kijk uit je doppen, Knus & Krokant!"';
        v.speechTimer = 3.5;
      }

      return {
        relativeSpeed: Math.max(1.0, relativeSpeed),
        van: v
      };
    }

    return null;
  }

  // Dynamic collision with opening driver door of Knus Expres
  checkDoorCollision(scooter) {
    const v = this.van;
    if (!v || !v.doorOpen) return false;

    // Driver door is on the driver's side (left of forward heading)
    const halfL = (v.w || 96) / 2;
    const halfW = (v.h || 46) / 2;
    const localDoorX = halfL - 25;
    const localDoorY = -halfW - 14;

    // Convert local door coordinate to world coordinates
    const worldDoorX = v.x + localDoorX * Math.cos(v.angle) - localDoorY * Math.sin(v.angle);
    const worldDoorY = v.y + localDoorX * Math.sin(v.angle) + localDoorY * Math.cos(v.angle);

    const dx = scooter.x - worldDoorX;
    const dy = scooter.y - worldDoorY;
    return (dx * dx + dy * dy < 28 * 28);
  }

  // Detecteert botsingen tussen de speler en rijdende/stilstaande AI-stadsauto's
  checkCarCollisions(scooter) {
    if (!scooter || !this.cars) return null;
    const allCars = [...this.cars];
    if (this.trafficJams) {
      for (const jam of this.trafficJams) {
        if (jam.active && jam.cars) allCars.push(...jam.cars);
      }
    }

    const scooterRadius = (scooter.vehicleType === 'van') ? 18 : 13;

    for (const car of allCars) {
      const cLength = car.length || 72;
      const cWidth = car.width || 36;
      const halfL = cLength / 2 + scooterRadius;
      const halfW = cWidth / 2 + scooterRadius;

      const cosA = Math.cos(-car.angle);
      const sinA = Math.sin(-car.angle);
      const dx = scooter.x - car.x;
      const dy = scooter.y - car.y;

      const localX = dx * cosA - dy * sinA;
      const localY = dx * sinA + dy * cosA;

      if (Math.abs(localX) < halfL && Math.abs(localY) < halfW) {
        // Botsing gedetecteerd! Bereken push-out vector
        const overlapX = halfL - Math.abs(localX);
        const overlapY = halfW - Math.abs(localY);

        let pushLocalX = 0;
        let pushLocalY = 0;
        if (overlapX < overlapY) {
          pushLocalX = localX > 0 ? overlapX : -overlapX;
        } else {
          pushLocalY = localY > 0 ? overlapY : -overlapY;
        }

        const worldPushX = pushLocalX * Math.cos(car.angle) - pushLocalY * Math.sin(car.angle);
        const worldPushY = pushLocalX * Math.sin(car.angle) + pushLocalY * Math.cos(car.angle);

        scooter.x += worldPushX * 1.1;
        scooter.y += worldPushY * 1.1;

        const relativeSpeed = Math.abs(scooter.speed) + Math.abs(car.speed || 0);
        scooter.speed *= -0.35;

        // Auto reactie: alarmlichten aan, remmen, schrikreactie
        car.hazardTimer = 4.5;
        car.speed = 0;
        car.isBraking = true;
        car.flashTimer = 0.5;

        if (car.speechTimer <= 0) {
          const crashQuotes = [
            '💥 "Kijk uit je doppen, bezorger!"',
            '⚠️ "Mijn bumper! Heb je een schadeformulier?!"',
            '📢 "Pas toch op! Dit kost me m\'n no-claim!"',
            '🚗💥 "Ho ho ho, rustig aan met die scooter!"'
          ];
          car.speechText = crashQuotes[Math.floor(Math.random() * crashQuotes.length)];
          car.speechTimer = 3.8;
        }

        // Als het een politiewagen is: activeer sirenes en start achtervolging (niet tijdens de rust na een boete)
        if (car.isPolice && car.state !== 'chase' && !this.policeSuppressed) {
          car.state = 'chase';
          car.sirenActive = true;
          car.speechText = '🚨 "Politie! Stop onmiddellijk aan de kant!"';
          car.speechTimer = 4.5;
        }

        return {
          relativeSpeed: Math.max(1.0, relativeSpeed),
          car
        };
      }
    }
    return null;
  }
}
